import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../services/authService';
import apiService from '../../services/api';
import { UserProfile, Quest } from '../../types/auth';
import QuestModal from '../../components/QuestModal/QuestModal';
import Navigation from '../../components/Navigation/Navigation';
import UploadFilesPage from '../UploadFilesPage/UploadFilesPage';
import TranscriptionPage from '../TranscriptionPage/TranscriptionPage';
import AllUsersPage from '../AllUsersPage/AllUsersPage';
import UserFeedbackPage from '../UserFeedbackPage/UserFeedbackPage';
import StoriesPage from '../StoriesPage/StoriesPage';
import { extractUsernameBeforeAt } from '../../utils/validation';
import { config } from '../../config';
import './DashboardPage.css';

const DashboardPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddPostModal, setShowAddPostModal] = useState(false);
  const [postForm, setPostForm] = useState({
    urls: [''],
    lrc_url: '',
    thumb_url: '',
    post_title: '',
    post_subtitle: '',
    quest_id: '',
    quest_title: '',
    tags: [] as string[],
    category: '',
    post_type: 'video',
    language: 'english',
  });

  const categoryMap: Record<string, string> = {
    devotional: 'Devotional',
    'self-help': 'Self Help',
    health: 'Health',
  };

  const categories = Object.keys(categoryMap);

  const [availableTags, setAvailableTags] = useState<
    Array<{
      id: string;
      name: string;
      image_url: string;
      text_color: string;
      language: string;
      created_at: string;
      updated_at: string;
    }>
  >([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [submittingPost, setSubmittingPost] = useState(false);
  const [editingPost, setEditingPost] = useState<{
    post_id: string;
    urls: string[];
    lrc_url: string;
    thumb_url: string;
    post_title: string;
    post_subtitle: string;
    quest_id: string;
    quest_title: string;
    tags: string[];
    category: string;
    post_type: string;
    language: string;
  } | null>(null);
  const [posts, setPosts] = useState<
    Array<{
      id: string;
      title: string;
      description: string;
      categories: string[];
      language: string;
      script: string;
      urls: string[];
      created_at: string;
      updated_at: string;
      tags: string[];
      post_type: string;
      duration: number;
      entity_id: string;
      author_id: string;
      lrc_url: string;
      hierarchical_category_path: string[];
    }>
  >([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [editingQuest, setEditingQuest] = useState<Quest | null>(null);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [tagForm, setTagForm] = useState({
    name: '',
    image_url: '',
    text_color: '',
    language: 'hinglish',
    version: 'v1' as 'v1',
  });
  const [submittingTag, setSubmittingTag] = useState(false);
  const [editingTag, setEditingTag] = useState<{
    id: string;
    name: string;
    image_url: string;
    text_color: string;
    language: string;
    version?: string;
  } | null>(null);
  const [showAddChallengeModal, setShowAddChallengeModal] = useState(false);
  const [challengeForm, setChallengeForm] = useState({
    icon_url: '',
    image_url: '',
    title: '',
    subtitle: '',
    ai_coach_context: '',
    emoji: '',
    language: 'English',
    share_text: '',
    share_image_url: '',
    is_public: true,
    category: '',
    tags: [] as string[],
    start_color: '',
    max_bucket: '100',
    min_bucket: '0',
  });
  const [submittingChallenge, setSubmittingChallenge] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState<{
    id: string;
    icon_url: string;
    image_url?: string;
    title: string;
    subtitle: string;
    emoji: string;
    language: string;
    share_text: string;
    share_image_url: string;
    is_public: boolean;
    category?: string;
    tags?: string[];
    start_color?: string;
    max_bucket?: string;
    min_bucket?: string;
  } | null>(null);
  const [challenges, setChallenges] = useState<
    Array<{
      id: string;
      icon_url: string;
      title: string;
      subtitle: string;
      emoji: string;
      language: string;
      share_text: string;
      share_image_url: string;
      is_public: boolean;
      is_deleted: boolean;
      created_at: string;
      updated_at: string;
      max_bucket?: string;
      min_bucket?: string;
    }>
  >([]);
  const [challengesLoading, setChallengesLoading] = useState(false);
  const [masteryChallenges, setMasteryChallenges] = useState<
    Array<{
      id: string;
      icon_url: string;
      title: string;
      subtitle: string;
      emoji: string;
      language: string;
      share_text: string;
      share_image_url: string;
      is_public: boolean;
      is_deleted: boolean;
      created_at: string;
      updated_at: string;
      max_bucket?: string;
      min_bucket?: string;
    }>
  >([]);
  const [masteryChallengesLoading, setMasteryChallengesLoading] =
    useState(false);
  const [challengeLanguageFilter, setChallengeLanguageFilter] = useState<
    string[]
  >(['english', 'hindi', 'hinglish']);
  const [masteryChallengeLanguageFilter, setMasteryChallengeLanguageFilter] =
    useState<string[]>(['english', 'hindi', 'hinglish']);
  const [showChallengeFilter, setShowChallengeFilter] = useState(false);
  const [showMasteryFilter, setShowMasteryFilter] = useState(false);
  const [showAddMasteryChallengeModal, setShowAddMasteryChallengeModal] =
    useState(false);
  const [masteryChallengeForm, setMasteryChallengeForm] = useState({
    icon_url: '',
    image_url: '',
    title: '',
    subtitle: '',
    ai_coach_context: '',
    emoji: '',
    language: 'english',
    share_text: '',
    share_image_url: '',
    is_public: true,
    category: '',
    tags: [] as string[],
    start_color: '',
    max_bucket: '100',
    min_bucket: '0',
    l0_category: '',
    l1_category: '',
    l2_category: '',
  });
  const [submittingMasteryChallenge, setSubmittingMasteryChallenge] =
    useState(false);
  const [editingMasteryChallenge, setEditingMasteryChallenge] = useState<{
    id: string;
    icon_url: string;
    image_url?: string;
    title: string;
    subtitle: string;
    emoji: string;
    language: string;
    share_text: string;
    share_image_url: string;
    is_public: boolean;
    category?: string;
    tags?: string[];
    start_color?: string;
    max_bucket?: string;
    min_bucket?: string;
  } | null>(null);
  const [filteredMasteryCategories, setFilteredMasteryCategories] = useState(
    [] as any[]
  );
  const [filteredTags, setFilteredTags] = useState<any[]>([]);
  const [filteredMasteryTags, setFilteredMasteryTags] = useState<any[]>([]);
  const [audioUrls, setAudioUrls] = useState<any[]>([]);
  const [audioUrlsLoading, setAudioUrlsLoading] = useState(false);
  const [showAddAudioModal, setShowAddAudioModal] = useState(false);
  const [audioForm, setAudioForm] = useState({
    language: '',
    purpose: '',
    audio_urls: [''],
  });
  const [submittingAudio, setSubmittingAudio] = useState(false);
  const [expandedPurpose, setExpandedPurpose] = useState<string | null>(null);
  const [editingAudioUrl, setEditingAudioUrl] = useState<{
    language: string;
    purpose: string;
    urlIndex: number;
    originalUrl: string;
  } | null>(null);
  const [editUrlValue, setEditUrlValue] = useState('');
  const [coaches, setCoaches] = useState<any[]>([]);
  const [coachesLoading, setCoachesLoading] = useState(false);
  const [showAddCoachModal, setShowAddCoachModal] = useState(false);
  const [editingCoach, setEditingCoach] = useState<any | null>(null);
  const [coachForm, setCoachForm] = useState({
    name: '',
    tagline: '',
    description: '',
    greeting: '',
    visibility: 'PUBLIC',
    avatar_image_url: '',
    banner_image_url: '',
    nsfw: false,
    language: 'english',
    active: true,
    model_name: 'gpt-3.5-turbo',
    temperature: 2,
    top_p: 1,
    top_k: 0,
    frequency_penalty: -2,
    presence_penalty: -2,
    repetition_penalty: 2,
    min_p: 1,
    top_a: 1,
    max_tokens: 1,
    gender: '',
    introduction: '',
    app_name: 'Quests App',
    quest_id: '',
    first_question: '',
    second_question: '',
    welcome_message: '',
    welcome_system_prompt: '',
    welcome_experiment: [] as Array<{
      prompt: string;
      message: string;
      bucket_list: number[];
    }>,
  });
  const [submittingCoach, setSubmittingCoach] = useState(false);
  const [rangePrompts, setRangePrompts] = useState<
    Array<{ min: string; max: string; prompt: string }>
  >([]);
  const [welcomeExperiments, setWelcomeExperiments] = useState<
    Array<{ prompt: string; message: string; bucket_list: string }>
  >([]);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    text_color: '',
    image_url: '',
    language: 'hindi',
    display_name: '',
    parent_category: '',
    emoji: '',
    level: '',
    version: 'v1' as 'v1',
    onboarding_flag: false,
    rank: 0,
  });
  const [submittingCategory, setSubmittingCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showAddExperimentModuleModal, setShowAddExperimentModuleModal] =
    useState(false);
  const [experimentModuleForm, setExperimentModuleForm] = useState({
    title: '',
    medal_share_image_url: '',
    link_url: '',
    medal_image_url: '',
    medal_joy_audio_url: '',
    medal_title: '',
    medal_subtitle: '',
    medal_share_text: '',
    medal_share_url: '',
    image_share_url: '',
    ai_coach_context: '',
    min_bucket: 0,
    max_bucket: 0,
    total_available_points: 0,
  });
  const [submittingExperimentModule, setSubmittingExperimentModule] =
    useState(false);
  const [editingExperimentModule, setEditingExperimentModule] = useState<
    any | null
  >(null);
  const [filteredCategories, setFilteredCategories] = useState<any[]>([]);
  const [experimentModules, setExperimentModules] = useState<any[]>([]);
  const [experimentModulesLoading, setExperimentModulesLoading] =
    useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showAddStoryModal, setShowAddStoryModal] = useState(false);
  const [editingStory, setEditingStory] = useState<any | null>(null);
  const [storyForm, setStoryForm] = useState({
    title: '',
    l0_category: '',
    l1_category: '',
    l2_category: '',
    tags: [] as string[],
    author: '',
    duration: 5,
    language: '',
    image_url: '',
    is_active: true,
    type: '',
    content_life_in_days: 7,
    script: '',
  });
  const [submittingStory, setSubmittingStory] = useState(false);
  const [l0Categories, setL0Categories] = useState<any[]>([]);
  const [l1Categories, setL1Categories] = useState<any[]>([]);
  const [l2Categories, setL2Categories] = useState<any[]>([]);
  const [showAddAuthorModal, setShowAddAuthorModal] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<any | null>(null);
  const [authorForm, setAuthorForm] = useState({
    name: '',
    profile_picture: '',
    bio: '',
  });
  const [submittingAuthor, setSubmittingAuthor] = useState(false);
  const [authors, setAuthors] = useState<any[]>([]);
  const [authorsLoading, setAuthorsLoading] = useState(false);
  const [notificationAction, setNotificationAction] = useState('');
  const [scheduleEntries, setScheduleEntries] = useState<
    { date: string; storyId: string; hour: string; minute: string }[]
  >([{ date: '', storyId: '', hour: '', minute: '' }]);
  const [submittingNotification, setSubmittingNotification] = useState(false);
  const [sendNowEntries, setSendNowEntries] = useState<
    { date: string; storyId: string; hour: string; minute: string }[]
  >([{ date: '', storyId: '', hour: '', minute: '' }]);
  const [submittingSendNow, setSubmittingSendNow] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState<
    {
      constant_id: string;
      constant_name: string;
      constant_value: string;
      type?: string;
    }[]
  >([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [editingNotificationStory, setEditingNotificationStory] = useState<
    any | null
  >(null);
  const [notificationStoryForm, setNotificationStoryForm] = useState<any>({});
  const [submittingNotificationStory, setSubmittingNotificationStory] =
    useState(false);
  const [storyDataLoading, setStoryDataLoading] = useState(false);

  useEffect(() => {
    const userId = profile?.id || user?.id;
    if (userId && !storyForm.author && !editingStory) {
      setStoryForm(prev => ({ ...prev, author: userId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.language-filter')) {
        setShowChallengeFilter(false);
        setShowMasteryFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    console.log('useEffect triggered, pathname:', location.pathname);
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('fetchData called for pathname:', location.pathname);
        if (location.pathname === '/dashboard') {
          const userProfile = await authService.getProfile();
          setProfile(userProfile);
        } else if (location.pathname === '/quests') {
          const response = await authService.getQuests();
          setQuests(response.data || []);
        } else if (location.pathname === '/feed') {
          await fetchPosts();
          // Fetch challenges and mastery challenges for the feed section
          if (challenges.length === 0) {
            await fetchChallenges();
          }
          if (masteryChallenges.length === 0) {
            await fetchMasteryChallenges();
          }
        } else if (location.pathname === '/daily-challenges') {
          await fetchChallenges();
        } else if (location.pathname === '/mastery-challenges') {
          await fetchMasteryChallenges();
        } else if (location.pathname === '/audio-urls') {
          await fetchAudioUrls();
        } else if (location.pathname === '/global-coach') {
          await fetchCoaches();
        } else if (location.pathname === '/category') {
          await fetchCategories();
        } else if (location.pathname === '/tags') {
          await fetchTags();
        } else if (location.pathname === '/experiment-modules') {
          await fetchExperimentModules();
        } else if (location.pathname === '/v3/authors') {
          await fetchAuthors();
        } else if (location.pathname === '/notification') {
          setNotificationsLoading(true);
          try {
            const data = await authService.getConstants();
            setScheduledNotifications(data);
          } finally {
            setNotificationsLoading(false);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        if (location.pathname === '/quests') {
          setError('Failed to load quests. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.pathname, quests.length]);

  const handleLogout = async () => {
    try {
      await logout(); // This will call the API and update the auth context
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, redirect to login
      navigate('/login');
    }
  };

  const handleNavigation = (route: string) => {
    navigate(route);
  };

  const isActiveRoute = (route: string) => {
    return location.pathname === route;
  };

  const handleAddQuest = () => {
    setEditingQuest(null); // Clear any existing quest data
    setShowModal(true);
  };

  const fetchTags = async () => {
    try {
      setTagsLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/tags-v2/all-tags`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tags: ${response.status}`);
      }

      const data = await response.json();
      setAvailableTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
      setError('Failed to load tags.');
    } finally {
      setTagsLoading(false);
    }
  };

  const fetchPosts = async () => {
    try {
      setPostsLoading(true);
      const token = localStorage.getItem('authToken');

      console.log(
        'Fetching posts - Auth token:',
        token ? 'Present' : 'Missing'
      );

      const headers: Record<string, string> = {
        accept: 'application/json',
      };

      if (token) {
        headers['x-auth-token'] = token;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/feed-post/`,
        {
          method: 'GET',
          headers,
        }
      );

      console.log('Posts API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Posts API error:', errorData);
        throw new Error(`Failed to fetch posts: ${response.status}`);
      }

      const data = await response.json();
      console.log('Posts API response data:', data);
      console.log('Posts array:', data.items);
      if (data.items && data.items.length > 0) {
        console.log('First post structure:', data.items[0]);
        console.log('First post thumbnail field:', data.items[0].urls);
      }
      setPosts(data.items || []);
      console.log('Posts state updated with', data.items?.length || 0, 'posts');
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError('Failed to load posts. Please check your authentication.');
    } finally {
      setPostsLoading(false);
    }
  };

  const handleEditPost = async (post: {
    post_id: string;
    video_url: string;
    thumbnail?: string;
    thumb_url?: string;
    post_title?: string;
    post_subtitle?: string;
    quest_id?: string;
    quest_title?: string;
    category?: string;
    tags: Array<{ tag_id: string; tag_name: string }>;
  }) => {
    // Ensure tags are loaded before processing
    let tagsToUse = availableTags;
    if (tagsToUse.length === 0) {
      await fetchTags();
      // After fetchTags completes, availableTags state will be updated
      // We need to wait for the next render cycle, so we'll use a workaround
      // by re-fetching the tags synchronously
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = { accept: 'application/json' };
      if (token) headers['x-auth-token'] = token;

      const response = await fetch(`${config.API_BASE_URL}/tags/`, {
        method: 'GET',
        headers,
      });
      const data = await response.json();
      tagsToUse = data.tags || [];
    }

    const thumbnailValue = post.thumbnail || post.thumb_url || '';

    // Map tag IDs from API response
    const tagIds = (post as any).tags
      ? Array.isArray((post as any).tags)
        ? (post as any).tags
            .map((tagId: string) => {
              const foundTag = tagsToUse.find(t => t.id === tagId);
              return foundTag?.id || tagId;
            })
            .filter((id: string) => id !== '')
        : post.tags.map(tag => tag.tag_id)
      : [];

    const formState = {
      urls: post.video_url ? [post.video_url] : [''],
      lrc_url: '',
      thumb_url: thumbnailValue,
      post_title: post.post_title || '',
      post_subtitle: post.post_subtitle || '',
      quest_id: post.quest_id || '',
      quest_title: post.quest_title || '',
      tags: tagIds,
      category: (post.category || '').trim(),
      post_type: 'video',
      language: 'english',
    };

    console.log('=== Setting form state ===');
    console.log('formState.category:', formState.category);
    console.log('post.category from API:', post.category);

    setEditingPost({
      post_id: post.post_id,
      ...formState,
    });
    setPostForm(formState);
    setShowAddPostModal(true);
    setError(null);
    setSubmittingPost(false);
  };

  const handleAddPost = () => {
    setEditingPost(null); // Clear editing state
    setShowAddPostModal(true);
    setError(null); // Clear any previous errors
    setSubmittingPost(false); // Reset submitting state
    if (availableTags.length === 0) {
      fetchTags();
    }
    // Quest data should already be loaded when user navigated to feed section
    // Fetch posts when opening the add post modal to refresh the feed
    fetchPosts();
  };

  const handleCloseAddPostModal = () => {
    setShowAddPostModal(false);
    setEditingPost(null); // Clear editing state
    setError(null); // Clear any errors when closing
    setSubmittingPost(false); // Reset submitting state
    setPostForm({
      urls: [''],
      lrc_url: '',
      thumb_url: '',
      post_title: '',
      post_subtitle: '',
      quest_id: '',
      quest_title: '',
      tags: [],
      category: '',
      post_type: 'video',
      language: 'english',
    });
  };

  const handlePostFormChange = (field: string, value: string | string[]) => {
    setPostForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleQuestChange = (questId: string) => {
    const selectedQuest = quests.find(quest => quest.id === questId);
    setPostForm(prev => ({
      ...prev,
      quest_id: questId,
      quest_title: selectedQuest?.title || '',
    }));
  };

  const handleSubmitPost = async () => {
    try {
      setSubmittingPost(true);
      setError(null);

      if (!postForm.post_title) {
        setError('Title is required');
        return;
      }

      const tagNames = postForm.tags
        .map(tagId => {
          const tag = availableTags.find(t => t.id === tagId);
          return tag?.name || '';
        })
        .filter(name => name !== '');

      const payload = {
        title: postForm.post_title,
        description: postForm.post_subtitle || '',
        categories: postForm.category ? [postForm.category] : [],
        language: postForm.language,
        script: '',
        urls: postForm.urls.filter(url => url.trim() !== ''),
        tags: tagNames,
        entity_id: postForm.quest_id || '',
        author_id: '',
        lrc_url: postForm.lrc_url || '',
        post_type: postForm.post_type,
        duration: 0,
        hierarchical_category_path: [],
      };

      const isEditing = editingPost !== null;
      const url = isEditing
        ? `${process.env.REACT_APP_API_BASE_URL}/feed-post/${editingPost.post_id}`
        : `${process.env.REACT_APP_API_BASE_URL}/feed-post/`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${isEditing ? 'update' : 'create'} post: ${response.status}`
        );
      }

      await fetchPosts();
      handleCloseAddPostModal();
      setError(null);
    } catch (error) {
      console.error('Error submitting post:', error);
      setError('Failed to create post. Please try again.');
    } finally {
      setSubmittingPost(false);
    }
  };

  const handleEditQuest = (quest: Quest) => {
    setEditingQuest(quest);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingQuest(null);
  };

  const handleQuestSave = async (questData: Quest) => {
    try {
      if (editingQuest) {
        // Update existing quest
        console.log('Updating quest with data:', questData);
        console.log('Subscription fields in questData:', {
          subscription_image: questData.subscription_image,
          subscription_title: questData.subscription_title,
          subscription_subtitle: questData.subscription_subtitle,
        });
        const updatedQuest = await authService.updateQuest(
          editingQuest.id,
          questData
        );
        console.log('Received updated quest from API:', updatedQuest);

        // Update the quests array with the response from the API
        setQuests(prevQuests => {
          const newQuests = prevQuests.map(quest =>
            quest.id === editingQuest.id
              ? {
                  ...quest,
                  ...updatedQuest,
                  update_at: new Date().toISOString(),
                }
              : quest
          );
          console.log('Updated quests array:', newQuests);
          return newQuests;
        });
      } else {
        // Create new quest
        console.log('Creating new quest with data:', questData);
        const newQuest = await authService.createQuest(questData);
        console.log('Received new quest from API:', newQuest);

        setQuests(prevQuests => [...prevQuests, newQuest]);
      }
      handleModalClose();
    } catch (error) {
      console.error('Failed to save quest:', error);
      setError('Failed to save quest. Please try again.');
    }
  };

  const handleQuestCardClick = (questId: string) => {
    navigate(`/quests/${questId}`);
  };

  const handleAddTag = () => {
    setEditingTag(null);
    setShowAddTagModal(true);
    setError(null);
  };

  const handleEditTag = (tag: {
    id: string;
    name: string;
    image_url: string;
    text_color: string;
    language: string;
    version?: string;
  }) => {
    setEditingTag(tag);
    setTagForm({
      name: tag.name,
      image_url: tag.image_url,
      text_color: tag.text_color,
      language: tag.language,
      version: (tag.version as 'v1') || 'v1',
    });
    setShowAddTagModal(true);
    setError(null);
  };

  const handleCloseAddTagModal = () => {
    setShowAddTagModal(false);
    setEditingTag(null);
    setError(null);
    setSubmittingTag(false);
    setTagForm({
      name: '',
      image_url: '',
      text_color: '',
      language: 'hinglish',
      version: 'v1',
    });
  };

  const handleTagFormChange = (field: string, value: string) => {
    setTagForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const fetchChallenges = async () => {
    try {
      setChallengesLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${config.API_BASE_URL}/daily-challenges/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch challenges');
      const data = await response.json();
      setChallenges(data || []);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setChallengesLoading(false);
    }
  };

  const fetchMasteryChallenges = async () => {
    try {
      setMasteryChallengesLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${config.API_BASE_URL}/mastery-challenges/`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch mastery challenges');
      const data = await response.json();
      setMasteryChallenges(data || []);
    } catch (error) {
      console.error('Error fetching mastery challenges:', error);
    } finally {
      setMasteryChallengesLoading(false);
    }
  };

  const handleAddChallenge = () => {
    setEditingChallenge(null);
    setChallengeForm({
      icon_url: '',
      image_url: '',
      title: '',
      subtitle: '',
      ai_coach_context: '',
      emoji: '',
      language: 'english',
      share_text: '',
      share_image_url: '',
      is_public: true,
      category: '',
      tags: [],
      start_color: '',
      max_bucket: '100',
      min_bucket: '0',
    });
    setShowAddChallengeModal(true);
    setError(null);
    if (availableTags.length === 0) {
      fetchTags();
    }
    if (categoryList.length === 0) {
      fetchCategories();
    }
    // Filter categories for default language
    const filtered = categoryList.filter(
      category => category.language === 'english'
    );
    setFilteredCategories(filtered);
  };

  const handleEditChallenge = async (challenge: {
    id: string;
    icon_url: string;
    title: string;
    subtitle: string;
    emoji: string;
    language: string;
    share_text: string;
    share_image_url: string;
    is_public: boolean;
    category?: string;
    tags?: string[];
    start_color?: string;
    max_bucket?: string;
    min_bucket?: string;
  }) => {
    setEditingChallenge(challenge);
    setChallengeForm({
      icon_url: challenge.icon_url,
      image_url: (challenge as any).image_url || '',
      title: challenge.title,
      subtitle: challenge.subtitle,
      ai_coach_context: (challenge as any).ai_coach_context || '',
      emoji: challenge.emoji,
      language: challenge.language,
      share_text: challenge.share_text,
      share_image_url: challenge.share_image_url,
      is_public: challenge.is_public,
      category: challenge.category || '',
      tags: challenge.tags || [],
      start_color: challenge.start_color || '',
      max_bucket: challenge.max_bucket || '100',
      min_bucket: challenge.min_bucket || '0',
    });
    setShowAddChallengeModal(true);
    setError(null);
    if (availableTags.length === 0) {
      fetchTags();
    }
    if (categoryList.length === 0) {
      await fetchCategories();
    }
    // Wait for categories to load if needed
    let categoriesToFilter = categoryList;
    if (categoryList.length === 0) {
      categoriesToFilter = await fetchCategories();
    }
    // Filter categories for selected language
    const filtered = categoriesToFilter.filter(
      category => category.language === challenge.language.toLowerCase()
    );
    setFilteredCategories(filtered);
    // Load tags for the selected language
    await fetchTagsByLanguage(challenge.language.toLowerCase());
  };

  const handleCloseAddChallengeModal = () => {
    setShowAddChallengeModal(false);
    setEditingChallenge(null);
    setError(null);
    setSubmittingChallenge(false);
    setChallengeForm({
      icon_url: '',
      image_url: '',
      title: '',
      subtitle: '',
      ai_coach_context: '',
      emoji: '',
      language: 'English',
      share_text: '',
      share_image_url: '',
      is_public: true,
      category: '',
      tags: [],
      start_color: '',
      max_bucket: '100',
      min_bucket: '0',
    });
  };

  const handleChallengeFormChange = (
    field: string,
    value: string | boolean | string[]
  ) => {
    setChallengeForm(prev => ({ ...prev, [field]: value }));

    // Filter categories and fetch tags when language changes
    if (field === 'language') {
      const filtered = categoryList.filter(
        category => category.language === value.toString().toLowerCase()
      );
      setFilteredCategories(filtered);

      // Fetch tags for the selected language
      fetchTagsByLanguage(value.toString().toLowerCase());

      // Check if original category exists in new language filter
      const originalCategory = editingChallenge?.category;
      const originalTags = editingChallenge?.tags || [];
      const originalLanguage = editingChallenge?.language;

      if (originalLanguage === value.toString() && originalCategory) {
        // Restore original category and tags when switching back to original language
        setChallengeForm(prev => ({
          ...prev,
          category: originalCategory,
          tags: originalTags,
        }));
      } else {
        // Clear category and tags when switching to different language
        setChallengeForm(prev => ({ ...prev, category: '', tags: [] }));
      }
    }
  };

  const handleSubmitChallenge = async () => {
    try {
      setSubmittingChallenge(true);
      setError(null);

      if (!challengeForm.title.trim()) {
        setError('Title is required');
        return;
      }

      const token = localStorage.getItem('authToken');
      const isEditing = editingChallenge !== null;
      const url = isEditing
        ? `${config.API_BASE_URL}/daily-challenges/${editingChallenge.id}`
        : `${config.API_BASE_URL}/daily-challenges/`;
      const method = isEditing ? 'PUT' : 'POST';

      const payload = {
        ...challengeForm,
        max_bucket: challengeForm.max_bucket || '100',
        min_bucket: challengeForm.min_bucket || '0',
      };

      const response = await fetch(url, {
        method,
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${isEditing ? 'update' : 'create'} challenge: ${response.status}`
        );
      }

      await fetchChallenges();
      handleCloseAddChallengeModal();
    } catch (error) {
      console.error(
        `Error ${editingChallenge ? 'updating' : 'creating'} challenge:`,
        error
      );
      setError(
        `Failed to ${editingChallenge ? 'update' : 'create'} challenge. Please try again.`
      );
    } finally {
      setSubmittingChallenge(false);
    }
  };

  const handleDeleteChallenge = async (challengeId: string) => {
    if (!window.confirm('Are you sure you want to delete this challenge?')) {
      return;
    }

    try {
      await authService.deleteDailyChallenge(challengeId);
      await fetchChallenges();
    } catch (error) {
      console.error('Error deleting challenge:', error);
      setError('Failed to delete challenge. Please try again.');
    }
  };

  const handleDeleteMasteryChallenge = async (challengeId: string) => {
    if (
      !window.confirm('Are you sure you want to delete this mastery challenge?')
    ) {
      return;
    }

    try {
      await authService.deleteMasteryChallenge(challengeId);
      await fetchMasteryChallenges();
    } catch (error) {
      console.error('Error deleting mastery challenge:', error);
      setError('Failed to delete mastery challenge. Please try again.');
    }
  };

  const handleAddMasteryChallenge = () => {
    setEditingMasteryChallenge(null);
    setMasteryChallengeForm({
      icon_url: '',
      image_url: '',
      title: '',
      subtitle: '',
      ai_coach_context: '',
      emoji: '',
      language: 'english',
      share_text: '',
      share_image_url: '',
      is_public: true,
      category: '',
      tags: [],
      start_color: '',
      max_bucket: '100',
      min_bucket: '0',
      l0_category: '',
      l1_category: '',
      l2_category: '',
    });
    setShowAddMasteryChallengeModal(true);
    setError(null);
    if (availableTags.length === 0) {
      fetchTags();
    }
    if (categoryList.length === 0) {
      fetchCategories();
    }
    // Filter categories for default language
    const filtered = categoryList.filter(
      category => category.language === 'english'
    );
    setFilteredMasteryCategories(filtered);
  };

  const handleEditMasteryChallenge = async (challenge: {
    id: string;
    icon_url: string;
    title: string;
    subtitle: string;
    emoji: string;
    language: string;
    share_text: string;
    share_image_url: string;
    is_public: boolean;
    category?: string;
    tags?: string[];
    start_color?: string;
    max_bucket?: string;
    min_bucket?: string;
  }) => {
    setEditingMasteryChallenge(challenge);
    setMasteryChallengeForm({
      icon_url: challenge.icon_url,
      image_url: (challenge as any).image_url || '',
      title: challenge.title,
      subtitle: challenge.subtitle,
      ai_coach_context: (challenge as any).ai_coach_context || '',
      emoji: challenge.emoji,
      language: challenge.language,
      share_text: challenge.share_text,
      share_image_url: challenge.share_image_url,
      is_public: challenge.is_public,
      category: challenge.category || '',
      tags: challenge.tags || [],
      start_color: challenge.start_color || '',
      max_bucket: challenge.max_bucket || '100',
      min_bucket: challenge.min_bucket || '0',
      l0_category: (challenge as any).l0_category || '',
      l1_category: (challenge as any).l1_category || '',
      l2_category: (challenge as any).l2_category || '',
    });
    setShowAddMasteryChallengeModal(true);
    setError(null);

    // Fetch L0, L1, L2 categories in parallel
    const token = localStorage.getItem('authToken');
    const headers = {
      accept: 'application/json',
      ...(token && { 'x-auth-token': token }),
    };
    Promise.all([
      fetch(`${config.API_BASE_URL}/category/level/l0`, { headers }).then(r =>
        r.json()
      ),
      fetch(`${config.API_BASE_URL}/category/level/l1`, { headers }).then(r =>
        r.json()
      ),
      fetch(`${config.API_BASE_URL}/category/level/l2`, { headers }).then(r =>
        r.json()
      ),
    ])
      .then(([l0, l1, l2]) => {
        setL0Categories(l0.data || l0 || []);
        setL1Categories(l1.data || l1 || []);
        setL2Categories(l2.data || l2 || []);
      })
      .catch(err => console.error('Error fetching level categories:', err));

    if (availableTags.length === 0) {
      fetchTags();
    }
    if (categoryList.length === 0) {
      await fetchCategories();
    }
    // Wait for categories to load if needed
    let categoriesToFilter = categoryList;
    if (categoryList.length === 0) {
      categoriesToFilter = await fetchCategories();
    }
    // Filter categories for selected language
    const filtered = categoriesToFilter.filter(
      category => category.language === challenge.language.toLowerCase()
    );
    setFilteredMasteryCategories(filtered);
    // Load tags for the selected language
    await fetchMasteryTagsByLanguage(challenge.language.toLowerCase());
  };

  const handleCloseAddMasteryChallengeModal = () => {
    setShowAddMasteryChallengeModal(false);
    setEditingMasteryChallenge(null);
    setError(null);
    setSubmittingMasteryChallenge(false);
  };

  const handleMasteryChallengeFormChange = (
    field: string,
    value: string | boolean | string[]
  ) => {
    setMasteryChallengeForm(prev => ({ ...prev, [field]: value }));

    // Filter categories and fetch tags when language changes
    if (field === 'language') {
      const filtered = categoryList.filter(
        category => category.language === value.toString().toLowerCase()
      );
      setFilteredMasteryCategories(filtered);

      // Fetch tags for the selected language
      fetchMasteryTagsByLanguage(value.toString().toLowerCase());

      // Check if original category exists in new language filter
      const originalCategory = editingMasteryChallenge?.category;
      const originalTags = editingMasteryChallenge?.tags || [];
      const originalLanguage = editingMasteryChallenge?.language;

      if (originalLanguage === value.toString() && originalCategory) {
        // Restore original category and tags when switching back to original language
        setMasteryChallengeForm(prev => ({
          ...prev,
          category: originalCategory,
          tags: originalTags,
        }));
      } else {
        // Clear category and tags when switching to different language
        setMasteryChallengeForm(prev => ({ ...prev, category: '', tags: [] }));
      }
    }
  };

  const fetchAudioUrls = async () => {
    try {
      setAudioUrlsLoading(true);
      const data = await authService.getAudioUrls();
      setAudioUrls(data);
    } catch (error) {
      console.error('Error fetching audio URLs:', error);
    } finally {
      setAudioUrlsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const data = await authService.getCategories();
      setCategoryList(data);
      return data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchExperimentModules = async () => {
    try {
      setExperimentModulesLoading(true);
      const response = await apiService.getExperimentDailyModules();
      setExperimentModules(response.data || []);
    } catch (error) {
      console.error('Error fetching experiment modules:', error);
      setError('Failed to load experiment modules.');
    } finally {
      setExperimentModulesLoading(false);
    }
  };

  const fetchTagsByLanguage = async (language: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        accept: 'application/json',
      };
      if (token) {
        headers['x-auth-token'] = token;
      }

      const response = await fetch(
        `${config.API_BASE_URL}/tags-v2/language/${language}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tags: ${response.status}`);
      }

      const data = await response.json();
      setFilteredTags(data || []);
    } catch (error) {
      console.error('Error fetching tags by language:', error);
      setFilteredTags([]);
    }
  };

  const fetchMasteryTagsByLanguage = async (language: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        accept: 'application/json',
      };
      if (token) {
        headers['x-auth-token'] = token;
      }

      const response = await fetch(
        `${config.API_BASE_URL}/tags-v2/language/${language}`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tags: ${response.status}`);
      }

      const data = await response.json();
      setFilteredMasteryTags(data || []);
    } catch (error) {
      console.error('Error fetching mastery tags by language:', error);
      setFilteredMasteryTags([]);
    }
  };

  const fetchCoaches = async () => {
    try {
      setCoachesLoading(true);
      const data = await authService.getCoachesByQuestId(
        '716ee4b3-40a4-4980-94b9-b756a154a9f3'
      );
      setCoaches(data.coaches || []);
    } catch (error) {
      console.error('Error fetching coaches:', error);
    } finally {
      setCoachesLoading(false);
    }
  };

  const fetchAuthors = async () => {
    try {
      setAuthorsLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${config.API_BASE_URL}/signup/authors`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch authors');
      const data = await response.json();
      setAuthors(data || []);
    } catch (error) {
      console.error('Error fetching authors:', error);
    } finally {
      setAuthorsLoading(false);
    }
  };

  const handleAddCoach = () => {
    setEditingCoach(null);
    setCoachForm({
      name: '',
      tagline: '',
      description: '',
      greeting: '',
      visibility: 'PUBLIC',
      avatar_image_url: '',
      banner_image_url: '',
      nsfw: false,
      language: 'english',
      active: true,
      model_name: 'gpt-3.5-turbo',
      temperature: 2,
      top_p: 1,
      top_k: 0,
      frequency_penalty: -2,
      presence_penalty: -2,
      repetition_penalty: 2,
      min_p: 1,
      top_a: 1,
      max_tokens: 1,
      gender: '',
      introduction: '',
      app_name: 'Quests App',
      quest_id: '',
      first_question: '',
      second_question: '',
      welcome_message: '',
      welcome_system_prompt: '',
      welcome_experiment: [],
    });
    setRangePrompts([]);
    setWelcomeExperiments([]);
    setShowAddCoachModal(true);
  };

  const handleEditCoach = (coach: any) => {
    setEditingCoach(coach);
    setCoachForm({
      name: coach.name || '',
      tagline: coach.tagline || '',
      description: coach.description || '',
      greeting: coach.greeting || '',
      visibility: coach.visibility || 'PUBLIC',
      avatar_image_url: coach.avatar_image_url || '',
      banner_image_url: coach.banner_image_url || '',
      nsfw: coach.nsfw || false,
      language: coach.language || 'english',
      active: coach.active !== undefined ? coach.active : true,
      model_name: coach.model_name || 'gpt-3.5-turbo',
      temperature: coach.temperature !== undefined ? coach.temperature : 2,
      top_p: coach.top_p !== undefined ? coach.top_p : 1,
      top_k: coach.top_k !== undefined ? coach.top_k : 0,
      frequency_penalty:
        coach.frequency_penalty !== undefined ? coach.frequency_penalty : -2,
      presence_penalty:
        coach.presence_penalty !== undefined ? coach.presence_penalty : -2,
      repetition_penalty:
        coach.repetition_penalty !== undefined ? coach.repetition_penalty : 2,
      min_p: coach.min_p !== undefined ? coach.min_p : 1,
      top_a: coach.top_a !== undefined ? coach.top_a : 1,
      max_tokens: coach.max_tokens !== undefined ? coach.max_tokens : 1,
      gender: coach.gender || '',
      introduction: coach.introduction || '',
      app_name: coach.app_name || 'Quests App',
      quest_id: coach.quest_id || '',
      first_question: coach.first_question || '',
      second_question: coach.second_question || '',
      welcome_message: coach.welcome_message || '',
      welcome_system_prompt: coach.welcome_system_prompt || '',
      welcome_experiment: coach.welcome_experiment || [],
    });
    // Populate welcome experiments from API response
    if (coach.welcome_experiment && Array.isArray(coach.welcome_experiment)) {
      setWelcomeExperiments(
        coach.welcome_experiment.map((exp: any) => ({
          prompt: exp.prompt || '',
          message: exp.message || '',
          bucket_list: Array.isArray(exp.bucket_list)
            ? exp.bucket_list.join(', ')
            : '',
        }))
      );
    } else {
      setWelcomeExperiments([]);
    }
    // Populate range prompts from API response
    if (coach.prompts && Array.isArray(coach.prompts)) {
      setRangePrompts(
        coach.prompts.map((p: any) => ({
          min: p.lower_limit !== undefined ? String(p.lower_limit) : '',
          max: p.upper_limit !== undefined ? String(p.upper_limit) : '',
          prompt: p.prompt || '',
        }))
      );
    } else {
      setRangePrompts([]);
    }
    setShowAddCoachModal(true);
  };

  const handleCloseCoachModal = () => {
    setShowAddCoachModal(false);
    setEditingCoach(null);
    setError(null);
    setSubmittingCoach(false);
    setRangePrompts([]);
    setWelcomeExperiments([]);
  };

  const handleCoachFormChange = (field: string, value: any) => {
    setCoachForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddRangePrompt = () => {
    setRangePrompts(prev => [...prev, { min: '', max: '', prompt: '' }]);
  };

  const handleRangePromptChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setRangePrompts(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveRangePrompt = (index: number) => {
    setRangePrompts(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddWelcomeExperiment = () => {
    setWelcomeExperiments(prev => [
      ...prev,
      { prompt: '', message: '', bucket_list: '' },
    ]);
  };

  const handleWelcomeExperimentChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setWelcomeExperiments(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleRemoveWelcomeExperiment = (index: number) => {
    setWelcomeExperiments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitCoach = async () => {
    try {
      setSubmittingCoach(true);
      setError(null);

      if (!coachForm.name.trim()) {
        setError('Coach name is required');
        return;
      }

      const baseCoachData = {
        ...coachForm,
        visibility: coachForm.visibility as 'PUBLIC' | 'PRIVATE',
        entity_id: '716ee4b3-40a4-4980-94b9-b756a154a9f3',
        tags: {},
        attributes: {},
        prompts: rangePrompts.map(rp => ({
          lower_limit: rp.min,
          upper_limit: rp.max,
          prompt: rp.prompt,
        })),
        welcome_experiment: welcomeExperiments.map(exp => ({
          prompt: exp.prompt,
          message: exp.message,
          bucket_list: exp.bucket_list
            .split(',')
            .map(n => parseInt(n.trim()))
            .filter(n => !isNaN(n)),
        })),
      };

      if (editingCoach) {
        await authService.updateCoach(editingCoach.id, baseCoachData);
      } else {
        const { quest_id, ...createCoachData } = baseCoachData;
        await authService.createCoach(createCoachData);
      }

      await fetchCoaches();
      handleCloseCoachModal();
    } catch (error) {
      console.error('Error submitting coach:', error);
      setError(
        `Failed to ${editingCoach ? 'update' : 'create'} coach. Please try again.`
      );
    } finally {
      setSubmittingCoach(false);
    }
  };

  const handleAddAudio = (prefillPurpose?: string) => {
    setShowAddAudioModal(true);
    setAudioForm({
      language: '',
      purpose: prefillPurpose || '',
      audio_urls: [''],
    });
  };

  const handleAudioFormChange = (field: string, value: any) => {
    setAudioForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddAudioUrlField = () => {
    setAudioForm(prev => ({ ...prev, audio_urls: [...prev.audio_urls, ''] }));
  };

  const handleAudioUrlChange = (index: number, value: string) => {
    const newUrls = [...audioForm.audio_urls];
    newUrls[index] = value;
    setAudioForm(prev => ({ ...prev, audio_urls: newUrls }));
  };

  const handleSubmitAudio = async () => {
    try {
      setSubmittingAudio(true);
      const purposeKey = audioForm.purpose.replace(/\s+/g, '_').toLowerCase();
      const existingPurposeData = audioUrls
        .filter(item => item.purpose === purposeKey)
        .find(item => item.language === audioForm.language.toLowerCase());

      const payload = {
        language: audioForm.language.toLowerCase(),
        purpose: purposeKey,
        audio_urls: existingPurposeData
          ? [...existingPurposeData.audio_urls, ...audioForm.audio_urls]
          : audioForm.audio_urls,
      };
      await authService.createAudioUrl(payload);
      await fetchAudioUrls();
      setShowAddAudioModal(false);
      setAudioForm({ language: '', purpose: '', audio_urls: [''] });
    } catch (error) {
      console.error('Error creating audio URL:', error);
      setError('Failed to create audio URL');
    } finally {
      setSubmittingAudio(false);
    }
  };

  const handleEditAudioUrl = (
    language: string,
    purpose: string,
    urlIndex: number,
    url: string
  ) => {
    setEditingAudioUrl({ language, purpose, urlIndex, originalUrl: url });
    setEditUrlValue(url);
  };

  const handleSaveAudioUrl = async () => {
    if (!editingAudioUrl) return;

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const item = audioUrls.find(
        item =>
          item.language === editingAudioUrl.language &&
          item.purpose === editingAudioUrl.purpose
      );

      if (!item) return;

      const updatedUrls = [...item.audio_urls];
      updatedUrls[editingAudioUrl.urlIndex] = editUrlValue;

      const response = await fetch(
        `${config.API_BASE_URL}/joy-audio-url/joy-audio-urls/${editingAudioUrl.language}/${editingAudioUrl.purpose}`,
        {
          method: 'PUT',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
          body: JSON.stringify({ audio_urls: updatedUrls }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update audio URL: ${response.status}`);
      }

      await fetchAudioUrls();
      setEditingAudioUrl(null);
      setEditUrlValue('');
    } catch (error) {
      console.error('Error updating audio URL:', error);
      setError('Failed to update audio URL. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingAudioUrl(null);
    setEditUrlValue('');
  };

  const handleDeleteAudioUrl = async (language: string, purpose: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(
        `${config.API_BASE_URL}/joy-audio-url/joy-audio-urls/${language}/${purpose}`,
        {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
            'x-auth-token': token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete audio URLs: ${response.status}`);
      }

      await fetchAudioUrls();
    } catch (error) {
      console.error('Error deleting audio URLs:', error);
      setError('Failed to delete audio URLs. Please try again.');
    }
  };

  const handleSubmitMasteryChallenge = async () => {
    try {
      setSubmittingMasteryChallenge(true);
      setError(null);

      if (!masteryChallengeForm.title.trim()) {
        setError('Title is required');
        return;
      }

      const token = localStorage.getItem('authToken');
      const isEditing = editingMasteryChallenge !== null;
      const url = isEditing
        ? `${config.API_BASE_URL}/mastery-challenges/${editingMasteryChallenge.id}`
        : `${config.API_BASE_URL}/mastery-challenges/`;
      const method = isEditing ? 'PUT' : 'POST';

      const payload = {
        ...masteryChallengeForm,
        max_bucket: masteryChallengeForm.max_bucket || '100',
        min_bucket: masteryChallengeForm.min_bucket || '0',
      };

      const response = await fetch(url, {
        method,
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${isEditing ? 'update' : 'create'} mastery challenge: ${response.status}`
        );
      }

      await fetchMasteryChallenges();
      handleCloseAddMasteryChallengeModal();
    } catch (error) {
      console.error(
        `Error ${editingMasteryChallenge ? 'updating' : 'creating'} mastery challenge:`,
        error
      );
      setError(
        `Failed to ${editingMasteryChallenge ? 'update' : 'create'} mastery challenge. Please try again.`
      );
    } finally {
      setSubmittingMasteryChallenge(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowAddCategoryModal(true);
    setError(null);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      text_color: category.text_color,
      image_url: category.image_url,
      language: category.language || 'hindi',
      display_name: category.display_name || '',
      parent_category: category.parent_category || '',
      emoji: category.emoji || '',
      level: category.level || '',
      version: category.version || 'v1',
      onboarding_flag: category.onboarding_flag || false,
      rank: category.rank || 0,
    });
    setShowAddCategoryModal(true);
    setError(null);
  };

  const handleCloseCategoryModal = () => {
    setShowAddCategoryModal(false);
    setEditingCategory(null);
    setError(null);
    setSubmittingCategory(false);
    setCategoryForm({
      name: '',
      text_color: '',
      image_url: '',
      language: 'hindi',
      display_name: '',
      parent_category: '',
      emoji: '',
      level: '',
      version: 'v1',
      onboarding_flag: false,
      rank: 0,
    });
  };

  const handleCategoryFormChange = (
    field: string,
    value: string | boolean | number
  ) => {
    setCategoryForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddExperimentModule = () => {
    setEditingExperimentModule(null);
    setShowAddExperimentModuleModal(true);
    setError(null);
  };

  const handleUploadExcel = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = e => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setSelectedFile(file);
        setShowConfirmModal(true);
      }
    };
    input.click();
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    setShowConfirmModal(false);
    setUploadingExcel(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        accept: 'application/json',
      };

      if (token) {
        headers['x-auth-token'] = token;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/scripts/challenge-post-creation`,
        {
          method: 'POST',
          headers,
          body: formData,
        }
      );

      const result = await response.json();
      setUploadResult(result);
      setShowUploadModal(true);
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadResult({ error: 'Upload failed. Please try again.' });
      setShowUploadModal(true);
    } finally {
      setUploadingExcel(false);
      setSelectedFile(null);
    }
  };

  const handleEditExperimentModule = (module: any) => {
    setEditingExperimentModule(module);
    setExperimentModuleForm({
      title: module.title,
      medal_share_image_url: module.medal_share_image_url || '',
      link_url: module.link_url || '',
      medal_image_url: module.medal_image_url || '',
      medal_joy_audio_url: module.medal_joy_audio_url || '',
      medal_title: module.medal_title || '',
      medal_subtitle: module.medal_subtitle || '',
      medal_share_text: module.medal_share_text || '',
      medal_share_url: module.medal_share_url || '',
      image_share_url: module.image_share_url || '',
      ai_coach_context: module.ai_coach_context || '',
      min_bucket: module.min_bucket || 0,
      max_bucket: module.max_bucket || 0,
      total_available_points: module.total_available_points || 0,
    });
    setShowAddExperimentModuleModal(true);
    setError(null);
  };

  const handleCloseExperimentModuleModal = () => {
    setShowAddExperimentModuleModal(false);
    setEditingExperimentModule(null);
    setError(null);
    setSubmittingExperimentModule(false);
    setExperimentModuleForm({
      title: '',
      medal_share_image_url: '',
      link_url: '',
      medal_image_url: '',
      medal_joy_audio_url: '',
      medal_title: '',
      medal_subtitle: '',
      medal_share_text: '',
      medal_share_url: '',
      image_share_url: '',
      ai_coach_context: '',
      min_bucket: 0,
      max_bucket: 0,
      total_available_points: 0,
    });
  };

  const handleExperimentModuleFormChange = (
    field: string,
    value: string | number
  ) => {
    setExperimentModuleForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitExperimentModule = async () => {
    try {
      setSubmittingExperimentModule(true);
      setError(null);

      if (!experimentModuleForm.title.trim()) {
        setError('Title is required');
        return;
      }

      const payload = {
        ...experimentModuleForm,
        total_available_points:
          parseInt(String(experimentModuleForm.total_available_points)) || 0,
      };

      if (editingExperimentModule) {
        await apiService.updateExperimentDailyModule(
          editingExperimentModule.id,
          payload
        );
      } else {
        await apiService.createExperimentDailyModule(payload);
      }
      await fetchExperimentModules();
      handleCloseExperimentModuleModal();
    } catch (error) {
      console.error('Error saving experiment module:', error);
      setError(
        `Failed to ${editingExperimentModule ? 'update' : 'create'} experiment module. Please try again.`
      );
    } finally {
      setSubmittingExperimentModule(false);
    }
  };

  const handleSubmitCategory = async () => {
    try {
      setSubmittingCategory(true);
      setError(null);

      if (!categoryForm.name.trim() || !categoryForm.level.trim()) {
        setError('validation');
        return;
      }

      const payload = {
        ...categoryForm,
        parent_category: categoryForm.parent_category || null,
      };

      if (editingCategory) {
        await authService.updateCategory(editingCategory.id, payload);
      } else {
        await authService.createCategory(payload);
      }

      await fetchCategories();
      handleCloseCategoryModal();
    } catch (error) {
      console.error('Error saving category:', error);
      setError(
        `Failed to ${editingCategory ? 'update' : 'create'} category. Please try again.`
      );
    } finally {
      setSubmittingCategory(false);
    }
  };

  const handleSubmitTag = async () => {
    try {
      setSubmittingTag(true);
      setError(null);

      // Validate form data
      if (!tagForm.name.trim()) {
        setError('Please enter a tag name');
        return;
      }

      const isEditing = editingTag !== null;
      const url = isEditing
        ? `${config.API_BASE_URL}/tags-v2/${editingTag.id}`
        : `${config.API_BASE_URL}/tags-v2/`;
      const method = isEditing ? 'PUT' : 'POST';

      // Prepare the request payload
      const payload = {
        name: tagForm.name.trim(),
        image_url: tagForm.image_url.trim(),
        text_color: tagForm.text_color.trim(),
        language: tagForm.language,
        version: tagForm.version,
      };

      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        accept: 'application/json',
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['x-auth-token'] = token;
      }

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${isEditing ? 'update' : 'create'} tag: ${response.status}`
        );
      }

      // Refresh tags list after successful operation
      await fetchTags();

      // Close modal and reset form on success
      handleCloseAddTagModal();
      setError(null);
    } catch (error) {
      console.error(
        `Error ${editingTag ? 'updating' : 'creating'} tag:`,
        error
      );
      setError(
        `Failed to ${editingTag ? 'update' : 'create'} tag. Please try again.`
      );
    } finally {
      setSubmittingTag(false);
    }
  };

  const handleEditStory = (story: any) => {
    setEditingStory(story);
    setStoryForm({
      title: story.title,
      l0_category: story.l0_category,
      l1_category: story.l1_category,
      l2_category: story.l2_category,
      tags: story.tags || [],
      author: story.author,
      duration: story.duration,
      language: story.language,
      image_url: story.image_url,
      is_active: story.is_active,
      type: story.type || '',
      content_life_in_days:
        (story as any).content_life_in_days ||
        (story as any).story_life_in_days ||
        7,
      script: story.script || '',
    });
    setShowAddStoryModal(true);
  };

  const handleDeleteStory = async (storyId: string) => {
    if (!window.confirm('Are you sure you want to delete this story?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${config.API_BASE_URL}/story/${storyId}`, {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete story: ${response.status}`);
      }

      window.location.reload();
    } catch (error) {
      console.error('Error deleting story:', error);
      setError('Failed to delete story. Please try again.');
    }
  };

  const handleStoryFormChange = (
    field: string,
    value: string | string[] | number | boolean
  ) => {
    setStoryForm(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (showAddStoryModal && availableTags.length === 0) {
      fetchTags();
    }
    if (showAddStoryModal && authors.length === 0) {
      fetchAuthors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddStoryModal]);

  const fetchCategoriesByLevel = async (level: string, language: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${config.API_BASE_URL}/category/level/${level}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );
      if (!response.ok) return [];
      const data = await response.json();
      return data.filter((cat: any) => cat.language === language);
    } catch (error) {
      console.error(`Error fetching ${level} categories:`, error);
      return [];
    }
  };

  useEffect(() => {
    if (storyForm.language) {
      fetchCategoriesByLevel('l0', storyForm.language).then(setL0Categories);
      fetchCategoriesByLevel('l1', storyForm.language).then(categories => {
        if (storyForm.l0_category) {
          const filtered = categories.filter(
            (cat: any) => cat.parent_category === storyForm.l0_category
          );
          setL1Categories(filtered);
        } else {
          setL1Categories(categories);
        }
      });
      fetchCategoriesByLevel('l2', storyForm.language).then(categories => {
        if (storyForm.l1_category) {
          const filtered = categories.filter(
            (cat: any) => cat.parent_category === storyForm.l1_category
          );
          setL2Categories(filtered);
        } else {
          setL2Categories(categories);
        }
      });
    }
  }, [storyForm.language, storyForm.l0_category, storyForm.l1_category]);

  const handleSubmitStory = async () => {
    try {
      setSubmittingStory(true);
      setError(null);

      if (
        !storyForm.title.trim() ||
        !storyForm.l0_category.trim() ||
        !storyForm.l1_category.trim() ||
        !storyForm.l2_category.trim() ||
        !storyForm.author.trim() ||
        !storyForm.duration
      ) {
        setError('validation');
        return;
      }

      const payload = {
        title: storyForm.title,
        type: storyForm.type,
        l0_category: storyForm.l0_category,
        l1_category: storyForm.l1_category,
        l2_category: storyForm.l2_category,
        tags: storyForm.tags,
        author: storyForm.author || profile?.id || user?.id || '',
        duration: storyForm.duration,
        language: storyForm.language.toLowerCase(),
        image_url: storyForm.image_url,
        is_active: storyForm.is_active,
        content_life_in_days: storyForm.content_life_in_days,
        script: storyForm.script,
      };

      const token = localStorage.getItem('authToken');
      const url = editingStory
        ? `${config.API_BASE_URL}/story/${editingStory.id}`
        : `${config.API_BASE_URL}/story/`;
      const method = editingStory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${editingStory ? 'update' : 'create'} story: ${response.status}`
        );
      }

      setShowAddStoryModal(false);
      setEditingStory(null);
      setStoryForm({
        title: '',
        l0_category: '',
        l1_category: '',
        l2_category: '',
        tags: [],
        author: '',
        duration: 5,
        language: '',
        image_url: '',
        is_active: true,
        type: '',
        content_life_in_days: 7,
        script: '',
      });
      window.location.reload();
    } catch (error) {
      console.error('Error submitting story:', error);
      setError(
        `Failed to ${editingStory ? 'update' : 'create'} story. Please try again.`
      );
    } finally {
      setSubmittingStory(false);
    }
  };

  const handleEditAuthor = (author: any) => {
    setEditingAuthor(author);
    setAuthorForm({
      name: author.name,
      profile_picture: author.profile_pic || '',
      bio: author.bio || '',
    });
    setShowAddAuthorModal(true);
  };

  const handleDeleteAuthor = async (authorId: string) => {
    if (!window.confirm('Are you sure you want to delete this author?')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${config.API_BASE_URL}/signup/author/${authorId}`,
        {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete author: ${response.status}`);
      }

      await fetchAuthors();
    } catch (error) {
      console.error('Error deleting author:', error);
      setError('Failed to delete author. Please try again.');
    }
  };

  const handleSubmitAuthor = async () => {
    try {
      setSubmittingAuthor(true);
      setError(null);

      if (!authorForm.name.trim() || !authorForm.profile_picture.trim()) {
        setError('Name and Profile Picture are required');
        return;
      }

      const userName = authorForm.name.trim().split(' ').join('').toLowerCase();
      const email = `${userName}@inkpenlabs.com`;

      const payload = editingAuthor
        ? {
            name: authorForm.name.trim(),
            profile_pic: authorForm.profile_picture.trim(),
            bio: authorForm.bio.trim(),
          }
        : {
            email,
            user_name: userName,
            name: authorForm.name.trim(),
            profile_pic: authorForm.profile_picture.trim(),
            bio: authorForm.bio.trim(),
          };

      const token = localStorage.getItem('authToken');
      const url = editingAuthor
        ? `${config.API_BASE_URL}/signup/author/${editingAuthor.id}`
        : `${config.API_BASE_URL}/signup/create-author`;
      const method = editingAuthor ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'x-auth-token': token }),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to ${editingAuthor ? 'update' : 'create'} author: ${response.status}`
        );
      }

      setShowAddAuthorModal(false);
      setEditingAuthor(null);
      setAuthorForm({ name: '', profile_picture: '', bio: '' });
      setError(null);
      await fetchAuthors();
    } catch (error) {
      console.error('Error submitting author:', error);
      setError(
        `Failed to ${editingAuthor ? 'update' : 'create'} author. Please try again.`
      );
    } finally {
      setSubmittingAuthor(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="loading-spinner">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const displayUser = profile || user;

  return (
    <div className="dashboard-page">
      {/* Top orange strip */}
      <div className="top-strip"></div>

      {/* Left Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2 className="dashboard-title">Admin Dashboard</h2>
        </div>

        <div className="sidebar-divider"></div>

        <div className="user-info">
          <div className="user-avatar-small">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="user-details">
            <h3 className="user-name">
              {displayUser?.name ||
                extractUsernameBeforeAt(displayUser?.username) ||
                'Kundan'}
            </h3>
            <p className="user-email">
              {displayUser?.email || 'admin@chatpat.in'}
            </p>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-button ${isActiveRoute('/dashboard') ? 'active' : ''}`}
            onClick={() => handleNavigation('/dashboard')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            My Profile
          </button>

          <button
            className={`nav-button ${isActiveRoute('/quests') ? 'active' : ''}`}
            onClick={() => handleNavigation('/quests')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 12l2 2 4-4" />
              <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
            </svg>
            Quest
          </button>

          <button
            className={`nav-button ${isActiveRoute('/upload') ? 'active' : ''}`}
            onClick={() => handleNavigation('/upload')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17,8 12,3 7,8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            Upload Files
          </button>

          <button
            className={`nav-button ${isActiveRoute('/feed') ? 'active' : ''}`}
            onClick={() => handleNavigation('/feed')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            Feed
          </button>

          <button
            className={`nav-button ${isActiveRoute('/transcription') ? 'active' : ''}`}
            onClick={() => handleNavigation('/transcription')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 1v22" />
              <rect x="6" y="8" width="12" height="8" rx="2" />
            </svg>
            Transcription
          </button>

          <button
            className={`nav-button ${isActiveRoute('/daily-challenges') ? 'active' : ''}`}
            onClick={() => handleNavigation('/daily-challenges')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22,4 12,14.01 9,11.01" />
            </svg>
            Daily Challenges
          </button>

          <button
            className={`nav-button ${isActiveRoute('/mastery-challenges') ? 'active' : ''}`}
            onClick={() => handleNavigation('/mastery-challenges')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            Mastery Challenges
          </button>

          <button
            className={`nav-button ${isActiveRoute('/audio-urls') ? 'active' : ''}`}
            onClick={() => handleNavigation('/audio-urls')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
            Audio URLs
          </button>

          <button
            className={`nav-button ${isActiveRoute('/global-coach') ? 'active' : ''}`}
            onClick={() => handleNavigation('/global-coach')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
              <path d="M6 12v5c3 0 5-1 8-1s5 1 8 1v-5" />
            </svg>
            Global Coach
          </button>

          <button
            className={`nav-button ${isActiveRoute('/all-users') ? 'active' : ''}`}
            onClick={() => handleNavigation('/all-users')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            All Users
          </button>

          <button
            className={`nav-button ${isActiveRoute('/user-feedback') ? 'active' : ''}`}
            onClick={() => handleNavigation('/user-feedback')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            User Feedback
          </button>

          <button
            className={`nav-button ${isActiveRoute('/category') ? 'active' : ''}`}
            onClick={() => handleNavigation('/category')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Category
          </button>

          <button
            className={`nav-button ${isActiveRoute('/tags') ? 'active' : ''}`}
            onClick={() => handleNavigation('/tags')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
            Tags
          </button>

          <button
            className={`nav-button ${isActiveRoute('/experiment-modules') ? 'active' : ''}`}
            onClick={() => handleNavigation('/experiment-modules')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2z" />
              <path d="M19 11h-4a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2z" />
              <path d="M14 7h-4a2 2 0 0 0-2 2v1a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
            </svg>
            Experiment Modules
          </button>

          <button
            className={`nav-button ${isActiveRoute('/v3') ? 'active' : ''}`}
            onClick={() => handleNavigation('/v3')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            V3
          </button>

          <button
            className={`nav-button ${isActiveRoute('/notification') ? 'active' : ''}`}
            onClick={() => handleNavigation('/notification')}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            Notification
          </button>
        </nav>

        <button className="logout-button" onClick={handleLogout}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16,17 21,12 16,7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {location.pathname === '/dashboard' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">My Profile</h1>
              </div>
            </div>

            <div className="profile-card">
              <div className="profile-header">
                <div className="profile-avatar">
                  <span>
                    {displayUser?.name?.charAt(0) ||
                      displayUser?.username?.charAt(0) ||
                      displayUser?.email?.charAt(0) ||
                      'K'}
                  </span>
                </div>
                <div className="profile-info">
                  <h2 className="profile-name">
                    {displayUser?.name ||
                      extractUsernameBeforeAt(displayUser?.username) ||
                      'Kundan'}
                  </h2>
                  <div className="role-badge">
                    {displayUser?.user_role || 'ADMIN'}
                  </div>
                </div>
              </div>

              <div className="profile-divider"></div>

              <div className="account-section">
                <h3 className="section-title">Account Information</h3>

                <div className="info-item">
                  <div className="info-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <div className="info-content">
                    <span className="info-label">User ID:</span>
                    <span className="info-value">
                      {displayUser?.id ||
                        'c4fd1283-520e-4720-bf57-abec2fa22680'}
                    </span>
                  </div>
                </div>

                <div className="info-item">
                  <div className="info-icon">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div className="info-content">
                    <span className="info-label">Email:</span>
                    <span className="info-value">
                      {displayUser?.email || 'admin@chatpat.in'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : location.pathname === '/upload' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Upload Files</h1>
                <p className="page-subtitle">Upload a file to the server</p>
              </div>
            </div>
            <UploadFilesPage />
          </>
        ) : location.pathname === '/feed' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Feed</h1>
                <p className="page-subtitle">
                  Stay updated with the latest content
                </p>
              </div>
              <button className="add-post-button" onClick={handleAddPost}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Post
              </button>
            </div>

            {postsLoading ? (
              <div className="feed-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading posts...</span>
              </div>
            ) : posts.length > 0 ? (
              <div className="posts-grid">
                {posts.map(post => {
                  return (
                    <div key={post.id} className="post-card">
                      <div className="post-content">
                        <div className="post-header">
                          <div className="post-date">
                            {new Date(post.created_at).toLocaleDateString()}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span className="post-type-badge">
                              {post.post_type}
                            </span>
                            <button
                              className="edit-post-button"
                              onClick={() =>
                                handleEditPost({
                                  post_id: post.id,
                                  video_url: post.urls?.[0] || '',
                                  thumb_url: post.urls?.[0] || '',
                                  post_title: post.title,
                                  post_subtitle: post.description,
                                  quest_id: post.entity_id,
                                  quest_title: '',
                                  category:
                                    post.hierarchical_category_path?.[0] || '',
                                  tags: (post.tags || []).map(
                                    (tag: string) => ({
                                      tag_id: tag,
                                      tag_name: tag,
                                    })
                                  ),
                                })
                              }
                              title="Edit post"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <h3 className="post-title">{post.title}</h3>
                        <p className="post-description">{post.description}</p>
                        <div className="post-meta">
                          {post.language && (
                            <span className="post-meta-item">
                              🌐 {post.language}
                            </span>
                          )}
                          {post.duration > 0 && (
                            <span className="post-meta-item">
                              ⏱️ {post.duration}s
                            </span>
                          )}
                        </div>
                        {post.tags && post.tags.length > 0 && (
                          <div className="post-tags">
                            {post.tags.map((tag: string, idx: number) => (
                              <span key={idx} className="post-tag">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="feed-empty-container">
                <div className="feed-empty-message">
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    <line x1="9" y1="9" x2="15" y2="9" />
                    <line x1="9" y1="13" x2="15" y2="13" />
                    <line x1="9" y1="17" x2="13" y2="17" />
                  </svg>
                  <h3>No posts available</h3>
                </div>
              </div>
            )}
          </>
        ) : location.pathname === '/mastery-challenges' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Mastery Challenges</h1>
                <p className="page-subtitle">
                  Manage mastery challenges for users
                </p>
              </div>
              <button
                className="add-challenge-button"
                onClick={handleAddMasteryChallenge}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Challenge
              </button>
            </div>
            {masteryChallengesLoading ? (
              <div className="challenges-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading mastery challenges...</span>
              </div>
            ) : masteryChallenges.length > 0 ? (
              <>
                <div className="language-filter">
                  <button
                    className={`filter-button ${showMasteryFilter ? 'active' : ''}`}
                    onClick={() => setShowMasteryFilter(!showMasteryFilter)}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
                    </svg>
                    Filter Languages
                    <span className="selected-languages">
                      ({masteryChallengeLanguageFilter.length})
                    </span>
                  </button>
                  {showMasteryFilter && (
                    <div className="filter-dropdown">
                      {['english', 'hindi', 'hinglish'].map(lang => (
                        <div
                          key={lang}
                          className="filter-option"
                          onClick={() => {
                            const isSelected =
                              masteryChallengeLanguageFilter.includes(lang);
                            setMasteryChallengeLanguageFilter(
                              isSelected
                                ? masteryChallengeLanguageFilter.filter(
                                    l => l !== lang
                                  )
                                : [...masteryChallengeLanguageFilter, lang]
                            );
                          }}
                        >
                          <input
                            type="checkbox"
                            className="filter-checkbox"
                            checked={masteryChallengeLanguageFilter.includes(
                              lang
                            )}
                            readOnly
                          />
                          <span>
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="challenges-grid">
                  {masteryChallenges
                    .filter(challenge =>
                      masteryChallengeLanguageFilter.includes(
                        challenge.language.toLowerCase()
                      )
                    )
                    .map(challenge => (
                      <div
                        key={challenge.id}
                        className="challenge-card"
                        onClick={() =>
                          navigate(`/mastery-challenges/${challenge.id}`)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {challenge.emoji && (
                          <div className="challenge-emoji">
                            {challenge.emoji}
                          </div>
                        )}
                        {challenge.icon_url && !challenge.emoji && (
                          <div className="challenge-icon">
                            <img
                              src={challenge.icon_url}
                              alt={challenge.title}
                            />
                          </div>
                        )}
                        <div className="challenge-content">
                          <div className="challenge-header">
                            <div>
                              <h3 className="challenge-title">
                                {challenge.title}
                              </h3>
                              <p className="challenge-id">
                                MC ID: {challenge.id}
                                <svg
                                  onClick={e => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(challenge.id);
                                  }}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <title>Copy MC ID</title>
                                  <rect
                                    x="9"
                                    y="9"
                                    width="13"
                                    height="13"
                                    rx="2"
                                    ry="2"
                                  />
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                              </p>
                            </div>
                            <div className="challenge-actions">
                              <button
                                className="edit-challenge-button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEditMasteryChallenge(challenge);
                                }}
                                title="Edit challenge"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                className="delete-challenge-button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteMasteryChallenge(challenge.id);
                                }}
                                title="Delete challenge"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="3,6 5,6 21,6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div className="challenge-meta">
                            <span
                              className={`challenge-status ${
                                challenge.is_public ? 'public' : 'private'
                              }`}
                            >
                              {challenge.is_public ? 'Public' : 'Private'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <div className="mastery-challenges-empty">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <h3>No Mastery Challenges Available</h3>
                <p>
                  There are currently no mastery challenges configured. Create
                  your first challenge to get started.
                </p>
              </div>
            )}
          </>
        ) : location.pathname === '/audio-urls' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Audio URLs</h1>
                <p className="page-subtitle">
                  Manage audio URLs for the application
                </p>
              </div>
              <button
                className="add-audio-button"
                onClick={() => handleAddAudio()}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Audio
              </button>
            </div>
            {audioUrlsLoading ? (
              <div className="audio-urls-loading">
                <div className="loading-spinner"></div>
                <span>Loading audio URLs...</span>
              </div>
            ) : audioUrls.length > 0 ? (
              <div className="audio-urls-container">
                {Object.entries(
                  audioUrls.reduce((acc: any, item: any) => {
                    if (!acc[item.purpose]) acc[item.purpose] = [];
                    acc[item.purpose].push(item);
                    return acc;
                  }, {})
                ).map(([purpose, items]: [string, any]) => (
                  <div key={purpose} className="audio-purpose-strip">
                    <div className="audio-strip-header">
                      <div
                        className="audio-strip-title"
                        onClick={() =>
                          setExpandedPurpose(
                            expandedPurpose === purpose ? null : purpose
                          )
                        }
                      >
                        <h3>{purpose}</h3>
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{
                            transform:
                              expandedPurpose === purpose
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        >
                          <polyline points="6,9 12,15 18,9" />
                        </svg>
                      </div>
                      <button
                        className="add-audio-strip-button"
                        onClick={e => {
                          e.stopPropagation();
                          handleAddAudio(purpose);
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        Add
                      </button>
                    </div>
                    {expandedPurpose === purpose && (
                      <div className="audio-languages-list">
                        {items.map((item: any, idx: number) => (
                          <div key={idx} className="audio-language-item">
                            <div className="audio-language-header">
                              <span className="audio-language">
                                {item.language}
                              </span>
                              <span className="audio-count">
                                {item.audio_urls.length} URLs
                              </span>
                            </div>
                            <div className="audio-urls-list">
                              {item.audio_urls.map(
                                (url: string, urlIdx: number) => {
                                  const isEditing =
                                    editingAudioUrl?.language ===
                                      item.language &&
                                    editingAudioUrl?.purpose === purpose &&
                                    editingAudioUrl?.urlIndex === urlIdx;

                                  return (
                                    <div
                                      key={urlIdx}
                                      className="audio-url-item"
                                    >
                                      {isEditing ? (
                                        <>
                                          <input
                                            type="url"
                                            value={editUrlValue}
                                            onChange={e =>
                                              setEditUrlValue(e.target.value)
                                            }
                                            className="audio-url-edit-input"
                                            autoFocus
                                          />
                                          <div className="audio-url-actions">
                                            <button
                                              className="audio-url-save-btn"
                                              onClick={handleSaveAudioUrl}
                                              title="Save"
                                            >
                                              <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                              >
                                                <path d="M20 6L9 17l-5-5" />
                                              </svg>
                                            </button>
                                            <button
                                              className="audio-url-cancel-btn"
                                              onClick={handleCancelEdit}
                                              title="Cancel"
                                            >
                                              <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                              >
                                                <line
                                                  x1="18"
                                                  y1="6"
                                                  x2="6"
                                                  y2="18"
                                                />
                                                <line
                                                  x1="6"
                                                  y1="6"
                                                  x2="18"
                                                  y2="18"
                                                />
                                              </svg>
                                            </button>
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="audio-url-link"
                                          >
                                            {url}
                                          </a>
                                          <div className="audio-url-actions">
                                            <button
                                              className="audio-url-edit-btn"
                                              onClick={() =>
                                                handleEditAudioUrl(
                                                  item.language,
                                                  purpose,
                                                  urlIdx,
                                                  url
                                                )
                                              }
                                              title="Edit URL"
                                            >
                                              <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                              >
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                              </svg>
                                            </button>
                                            <button
                                              className="audio-url-delete-btn"
                                              onClick={() =>
                                                handleDeleteAudioUrl(
                                                  item.language,
                                                  purpose
                                                )
                                              }
                                              title="Delete URL"
                                            >
                                              <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                              >
                                                <polyline points="3,6 5,6 21,6" />
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                <line
                                                  x1="10"
                                                  y1="11"
                                                  x2="10"
                                                  y2="17"
                                                />
                                                <line
                                                  x1="14"
                                                  y1="11"
                                                  x2="14"
                                                  y2="17"
                                                />
                                              </svg>
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="audio-urls-empty">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
                <h3>No Audio URLs</h3>
                <p>Add your first audio URL to get started.</p>
              </div>
            )}
          </>
        ) : location.pathname === '/transcription' ? (
          <>
            <Navigation showBack={false} />
            <TranscriptionPage />
          </>
        ) : location.pathname === '/all-users' ? (
          <>
            <Navigation showBack={false} />
            <AllUsersPage />
          </>
        ) : location.pathname === '/global-coach' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Global Coach</h1>
                <p className="page-subtitle">
                  Manage AI coaches for the application
                </p>
              </div>
              <button className="add-challenge-button" onClick={handleAddCoach}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Global Coach
              </button>
            </div>
            {coachesLoading ? (
              <div className="coaches-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading coaches...</span>
              </div>
            ) : coaches.length > 0 ? (
              <div className="coaches-grid">
                {coaches.map(coach => (
                  <div key={coach.id} className="coach-card">
                    <div className="coach-avatar">
                      {coach.avatar_image_url ? (
                        <img src={coach.avatar_image_url} alt={coach.name} />
                      ) : (
                        <div className="coach-avatar-placeholder">
                          <svg
                            width="40"
                            height="40"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="coach-content">
                      <div className="coach-header">
                        <h3 className="coach-name">{coach.name}</h3>
                        <button
                          className="edit-coach-button"
                          onClick={() => handleEditCoach(coach)}
                          title="Edit coach"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                      <p className="coach-tagline">{coach.tagline}</p>
                      <p className="coach-description">{coach.description}</p>
                      <div className="coach-meta">
                        <span className="coach-language">{coach.language}</span>
                        <span
                          className={`coach-status ${coach.active ? 'active' : 'inactive'}`}
                        >
                          {coach.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="coaches-empty">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <h3>No Coaches Available</h3>
                <p>There are currently no AI coaches configured.</p>
              </div>
            )}
          </>
        ) : location.pathname === '/daily-challenges' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Daily Challenges</h1>
                <p className="page-subtitle">
                  Manage daily challenges for users
                </p>
              </div>
              <button
                className="add-challenge-button"
                onClick={handleAddChallenge}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Challenge
              </button>
            </div>
            {challengesLoading ? (
              <div className="challenges-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading challenges...</span>
              </div>
            ) : challenges.length > 0 ? (
              <>
                <div className="language-filter">
                  <button
                    className={`filter-button ${showChallengeFilter ? 'active' : ''}`}
                    onClick={() => setShowChallengeFilter(!showChallengeFilter)}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
                    </svg>
                    Filter Languages
                    <span className="selected-languages">
                      ({challengeLanguageFilter.length})
                    </span>
                  </button>
                  {showChallengeFilter && (
                    <div className="filter-dropdown">
                      {['english', 'hindi', 'hinglish'].map(lang => (
                        <div
                          key={lang}
                          className="filter-option"
                          onClick={() => {
                            const isSelected =
                              challengeLanguageFilter.includes(lang);
                            setChallengeLanguageFilter(
                              isSelected
                                ? challengeLanguageFilter.filter(
                                    l => l !== lang
                                  )
                                : [...challengeLanguageFilter, lang]
                            );
                          }}
                        >
                          <input
                            type="checkbox"
                            className="filter-checkbox"
                            checked={challengeLanguageFilter.includes(lang)}
                            readOnly
                          />
                          <span>
                            {lang.charAt(0).toUpperCase() + lang.slice(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="challenges-grid">
                  {challenges
                    .filter(challenge =>
                      challengeLanguageFilter.includes(
                        challenge.language.toLowerCase()
                      )
                    )
                    .map(challenge => (
                      <div
                        key={challenge.id}
                        className="challenge-card"
                        onClick={() =>
                          navigate(`/daily-challenges/${challenge.id}`)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {challenge.emoji && (
                          <div className="challenge-emoji">
                            {challenge.emoji}
                          </div>
                        )}
                        {challenge.icon_url && !challenge.emoji && (
                          <div className="challenge-icon">
                            <img
                              src={challenge.icon_url}
                              alt={challenge.title}
                            />
                          </div>
                        )}
                        <div className="challenge-content">
                          <div className="challenge-header">
                            <div>
                              <h3 className="challenge-title">
                                {challenge.title}
                              </h3>
                              <p className="challenge-id">
                                DC ID: {challenge.id}
                                <svg
                                  onClick={e => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(challenge.id);
                                  }}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <title>Copy DC ID</title>
                                  <rect
                                    x="9"
                                    y="9"
                                    width="13"
                                    height="13"
                                    rx="2"
                                    ry="2"
                                  />
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                </svg>
                              </p>
                            </div>
                            <div className="challenge-actions">
                              <button
                                className="edit-challenge-button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEditChallenge(challenge);
                                }}
                                title="Edit challenge"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                className="delete-challenge-button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteChallenge(challenge.id);
                                }}
                                title="Delete challenge"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="3,6 5,6 21,6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div className="challenge-meta">
                            <span
                              className={`challenge-status ${
                                challenge.is_public ? 'public' : 'private'
                              }`}
                            >
                              {challenge.is_public ? 'Public' : 'Private'}
                            </span>
                          </div>
                          <div className="challenge-timestamps">
                            <span className="challenge-timestamp">
                              Created{' '}
                              {new Date(
                                challenge.created_at
                              ).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                            <span className="challenge-timestamp">
                              Updated{' '}
                              {new Date(
                                challenge.updated_at
                              ).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <div className="daily-challenges-empty">
                <svg
                  width="80"
                  height="80"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22,4 12,14.01 9,11.01" />
                </svg>
                <h3>No Daily Challenges Available</h3>
                <p>
                  There are currently no daily challenges configured. Create
                  your first challenge to get started.
                </p>
              </div>
            )}
          </>
        ) : location.pathname === '/user-feedback' ? (
          <>
            <Navigation showBack={false} />
            <UserFeedbackPage />
          </>
        ) : location.pathname === '/category' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Category Management</h1>
                <p className="page-subtitle">
                  Manage categories for your application
                </p>
              </div>
              <button
                className="add-challenge-button"
                onClick={handleAddCategory}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Category
              </button>
            </div>
            {categoriesLoading ? (
              <div className="categories-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading categories...</span>
              </div>
            ) : categoryList.length > 0 ? (
              <div className="challenges-grid">
                {categoryList.map(category => (
                  <div key={category.id} className="challenge-card">
                    {category.image_url ? (
                      <div className="challenge-icon">
                        <img src={category.image_url} alt={category.name} />
                      </div>
                    ) : (
                      <div className="challenge-emoji">📁</div>
                    )}
                    <div className="challenge-content">
                      <div className="challenge-header">
                        <div>
                          <h3 className="challenge-title">{category.name}</h3>
                          <p className="challenge-id">
                            ID: {category.id}
                            <svg
                              onClick={e => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(category.id);
                              }}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <title>Copy Category ID</title>
                              <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                              />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </p>
                        </div>
                        <div className="challenge-actions">
                          <button
                            className="edit-challenge-button"
                            onClick={e => {
                              e.stopPropagation();
                              handleEditCategory(category);
                            }}
                            title="Edit category"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="challenge-meta">
                        <span
                          className="challenge-status"
                          style={{ color: category.text_color }}
                        >
                          {category.text_color}
                        </span>
                        {category.onboarding_flag && (
                          <span
                            className="challenge-status"
                            style={{ marginLeft: '8px' }}
                          >
                            ✓ Onboarding
                          </span>
                        )}
                      </div>
                      <div className="challenge-timestamps">
                        <span className="challenge-timestamp">
                          Created{' '}
                          {new Date(category.created_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }
                          )}
                        </span>
                        <span className="challenge-timestamp">
                          Updated{' '}
                          {new Date(category.updated_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="categories-empty">
                <h3>No Categories Available</h3>
                <p>Create your first category to get started.</p>
              </div>
            )}
          </>
        ) : location.pathname === '/experiment-modules' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Experiment Modules</h1>
                <p className="page-subtitle">
                  Manage experiment modules for your application
                </p>
              </div>
              <button
                className="upload-excel-button"
                onClick={handleUploadExcel}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10,9 9,9 8,9" />
                </svg>
                Upload Excel
              </button>
              <button
                className="add-challenge-button"
                onClick={handleAddExperimentModule}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Module
              </button>
            </div>
            {experimentModulesLoading ? (
              <div className="tags-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading experiment modules...</span>
              </div>
            ) : experimentModules.length > 0 ? (
              <div className="challenges-grid">
                {experimentModules.map(module => (
                  <div
                    key={module.id}
                    className="challenge-card"
                    onClick={() => navigate(`/experiment-modules/${module.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="challenge-emoji">🧪</div>
                    <div className="challenge-content">
                      <div className="challenge-header">
                        <div>
                          <h3 className="challenge-title">{module.title}</h3>
                          <p className="challenge-id">
                            ID: {module.id}
                            <svg
                              onClick={e => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(module.id);
                              }}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <title>Copy Module ID</title>
                              <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                              />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </p>
                        </div>
                        <div className="challenge-actions">
                          <button
                            className="edit-challenge-button"
                            onClick={e => {
                              e.stopPropagation();
                              handleEditExperimentModule(module);
                            }}
                            title="Edit module"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="challenge-meta">
                        <span className="challenge-status">
                          Buckets: {module.min_bucket} - {module.max_bucket}
                        </span>
                      </div>
                      <div className="challenge-timestamps">
                        <span className="challenge-timestamp">
                          Created{' '}
                          {new Date(module.created_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }
                          )}
                        </span>
                        <span className="challenge-timestamp">
                          Updated{' '}
                          {new Date(module.updated_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tags-empty">
                <h3>No Experiment Modules Available</h3>
                <p>Create your first experiment module to get started.</p>
              </div>
            )}
          </>
        ) : location.pathname === '/v3' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">V3</h1>
                <p className="page-subtitle">Version 3 Features</p>
              </div>
            </div>
            <div className="challenges-grid">
              <div
                className="challenge-card"
                onClick={() => navigate('/category')}
                style={{ cursor: 'pointer' }}
              >
                <div className="challenge-emoji">🚀</div>
                <div className="challenge-content">
                  <h3 className="challenge-title">Categories</h3>
                  <p className="challenge-id">Manage categories</p>
                </div>
              </div>
              <div
                className="challenge-card"
                onClick={() => navigate('/tags')}
                style={{ cursor: 'pointer' }}
              >
                <div className="challenge-emoji">⚡</div>
                <div className="challenge-content">
                  <h3 className="challenge-title">Tags</h3>
                  <p className="challenge-id">Manage tags</p>
                </div>
              </div>
              <div
                className="challenge-card"
                onClick={() => navigate('/v3/stories')}
                style={{ cursor: 'pointer' }}
              >
                <div className="challenge-emoji">✨</div>
                <div className="challenge-content">
                  <h3 className="challenge-title">Stories</h3>
                  <p className="challenge-id">Manage stories</p>
                </div>
              </div>
              <div
                className="challenge-card"
                onClick={() => navigate('/v3/authors')}
                style={{ cursor: 'pointer' }}
              >
                <div className="challenge-emoji">✍️</div>
                <div className="challenge-content">
                  <h3 className="challenge-title">Authors</h3>
                  <p className="challenge-id">Manage authors</p>
                </div>
              </div>
            </div>
          </>
        ) : location.pathname === '/v3/stories' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Stories</h1>
                <p className="page-subtitle">
                  Manage stories for your application
                </p>
              </div>
              <button
                className="add-challenge-button"
                onClick={() => setShowAddStoryModal(true)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Story
              </button>
            </div>
            <StoriesPage
              onEditStory={handleEditStory}
              onDeleteStory={handleDeleteStory}
            />
          </>
        ) : location.pathname === '/v3/authors' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Authors</h1>
                <p className="page-subtitle">
                  Manage authors for your application
                </p>
              </div>
              <button
                className="add-challenge-button"
                onClick={() => setShowAddAuthorModal(true)}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Author
              </button>
            </div>
            {authorsLoading ? (
              <div className="challenges-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading authors...</span>
              </div>
            ) : authors.length > 0 ? (
              <div className="challenges-grid">
                {authors.map(author => (
                  <div key={author.id} className="challenge-card">
                    <div className="challenge-content">
                      <div className="challenge-header">
                        <div>
                          <h3 className="challenge-title">{author.name}</h3>
                          <p className="challenge-id">
                            ID: {author.id}
                            <svg
                              onClick={e => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(author.id);
                              }}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <title>Copy Author ID</title>
                              <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                              />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </p>
                          <p className="challenge-id">@{author.username}</p>
                          <p className="challenge-id">{author.email}</p>
                        </div>
                        <div className="challenge-actions">
                          <button
                            className="edit-challenge-button"
                            onClick={e => {
                              e.stopPropagation();
                              handleEditAuthor(author);
                            }}
                            title="Edit author"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            className="delete-challenge-button"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteAuthor(author.id);
                            }}
                            title="Delete author"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="3,6 5,6 21,6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="challenges-empty">
                <h3>No Authors Available</h3>
                <p>Create your first author to get started.</p>
              </div>
            )}
          </>
        ) : location.pathname === '/tags' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Tags Management</h1>
                <p className="page-subtitle">
                  Manage tags for your application
                </p>
              </div>
              <button className="add-challenge-button" onClick={handleAddTag}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Tag
              </button>
            </div>
            {tagsLoading ? (
              <div className="tags-loading-container">
                <div className="loading-spinner"></div>
                <span>Loading tags...</span>
              </div>
            ) : availableTags.length > 0 ? (
              <div className="challenges-grid">
                {availableTags.map(tag => (
                  <div key={tag.id} className="challenge-card">
                    {tag.image_url ? (
                      <div className="challenge-icon">
                        <img src={tag.image_url} alt={tag.name} />
                      </div>
                    ) : (
                      <div className="challenge-emoji">🏷️</div>
                    )}
                    <div className="challenge-content">
                      <div className="challenge-header">
                        <div>
                          <h3 className="challenge-title">{tag.name}</h3>
                          <p className="challenge-id">
                            ID: {tag.id}
                            <svg
                              onClick={e => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(tag.id);
                              }}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <title>Copy Tag ID</title>
                              <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                              />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </p>
                          <p className="challenge-id">
                            Language: {tag.language}
                          </p>
                        </div>
                        <div className="challenge-actions">
                          <button
                            className="edit-challenge-button"
                            onClick={e => {
                              e.stopPropagation();
                              handleEditTag(tag);
                            }}
                            title="Edit tag"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="challenge-meta">
                        <span
                          className="challenge-status"
                          style={{ color: `#${tag.text_color}` }}
                        >
                          {tag.language}
                        </span>
                      </div>
                      <div className="challenge-timestamps">
                        <span className="challenge-timestamp">
                          Created{' '}
                          {new Date(tag.created_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }
                          )}
                        </span>
                        <span className="challenge-timestamp">
                          Updated{' '}
                          {new Date(tag.updated_at).toLocaleDateString(
                            'en-US',
                            {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            }
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tags-empty">
                <h3>No Tags Available</h3>
                <p>Create your first tag to get started.</p>
              </div>
            )}
          </>
        ) : location.pathname === '/notification' ? (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Notifications</h1>
                <p className="page-subtitle">Manage and send notifications</p>
              </div>
            </div>
            <div style={{ marginTop: '24px' }}>
              <select
                className="form-input"
                value={notificationAction}
                onChange={e => {
                  setNotificationAction(e.target.value);
                  setScheduleEntries([
                    { date: '', storyId: '', hour: '', minute: '' },
                  ]);
                }}
                style={{ maxWidth: '350px' }}
              >
                <option value="" disabled>
                  Select an action
                </option>
                <option value="schedule">Schedule Manual Notification</option>
                <option value="send-now">Send Notification Now</option>
              </select>
            </div>

            {notificationAction === 'schedule' && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ marginBottom: '16px' }}>Schedule Notifications</h3>
                {scheduleEntries.map((entry, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={entry.date}
                        onChange={e => {
                          const updated = [...scheduleEntries];
                          updated[index].date = e.target.value;
                          setScheduleEntries(updated);
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Story ID</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter story ID"
                        value={entry.storyId}
                        onChange={e => {
                          const updated = [...scheduleEntries];
                          updated[index].storyId = e.target.value;
                          setScheduleEntries(updated);
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Hour (0-23)</label>
                      <input
                        type="number"
                        className="form-input"
                        min="0"
                        max="23"
                        placeholder="HH"
                        value={entry.hour}
                        onChange={e => {
                          const updated = [...scheduleEntries];
                          updated[index].hour = e.target.value;
                          setScheduleEntries(updated);
                        }}
                        style={{ width: '80px' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Minute (0-59)</label>
                      <input
                        type="number"
                        className="form-input"
                        min="0"
                        max="59"
                        placeholder="MM"
                        value={entry.minute}
                        onChange={e => {
                          const updated = [...scheduleEntries];
                          updated[index].minute = e.target.value;
                          setScheduleEntries(updated);
                        }}
                        style={{ width: '80px' }}
                      />
                    </div>
                    {scheduleEntries.length > 1 && (
                      <button
                        className="btn-secondary"
                        style={{ marginTop: '20px' }}
                        onClick={() =>
                          setScheduleEntries(
                            scheduleEntries.filter((_, i) => i !== index)
                          )
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <div
                  style={{ display: 'flex', gap: '12px', marginTop: '16px' }}
                >
                  <button
                    className="btn-secondary"
                    onClick={() =>
                      setScheduleEntries([
                        ...scheduleEntries,
                        { date: '', storyId: '', hour: '', minute: '' },
                      ])
                    }
                  >
                    + Add More
                  </button>
                  <button
                    className="btn-primary"
                    disabled={submittingNotification}
                    onClick={async () => {
                      const valid = scheduleEntries.filter(
                        e => e.date && e.storyId
                      );
                      if (valid.length === 0) {
                        alert('Please fill in at least one entry.');
                        return;
                      }
                      setSubmittingNotification(true);
                      try {
                        for (const entry of valid) {
                          const d = new Date(entry.date);
                          await authService.createConstant({
                            constant_name: 'manual story id',
                            constant_value: entry.storyId,
                            day: d.getDate(),
                            month: d.getMonth() + 1,
                            year: d.getFullYear(),
                            hour: parseInt(entry.hour, 10) || 0,
                            minute: parseInt(entry.minute, 10) || 0,
                            is_sent: false,
                            type: 'scheduled',
                          });
                        }
                        alert(
                          `${valid.length} notification(s) scheduled successfully!`
                        );
                        setScheduleEntries([
                          { date: '', storyId: '', hour: '', minute: '' },
                        ]);
                        const data = await authService.getConstants();
                        setScheduledNotifications(data);
                      } catch (err) {
                        alert('Failed to schedule notifications.');
                      } finally {
                        setSubmittingNotification(false);
                      }
                    }}
                  >
                    {submittingNotification ? 'Scheduling...' : 'Schedule All'}
                  </button>
                </div>
              </div>
            )}

            {notificationAction === 'send-now' && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ marginBottom: '16px' }}>Send Notification Now</h3>
                {sendNowEntries.map((entry, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={entry.date}
                        onChange={e => {
                          const updated = [...sendNowEntries];
                          updated[index].date = e.target.value;
                          setSendNowEntries(updated);
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Story ID</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter story ID"
                        value={entry.storyId}
                        onChange={e => {
                          const updated = [...sendNowEntries];
                          updated[index].storyId = e.target.value;
                          setSendNowEntries(updated);
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Hour (0-23)</label>
                      <input
                        type="number"
                        className="form-input"
                        min="0"
                        max="23"
                        placeholder="HH"
                        value={entry.hour}
                        onChange={e => {
                          const updated = [...sendNowEntries];
                          updated[index].hour = e.target.value;
                          setSendNowEntries(updated);
                        }}
                        style={{ width: '80px' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Minute (0-59)</label>
                      <input
                        type="number"
                        className="form-input"
                        min="0"
                        max="59"
                        placeholder="MM"
                        value={entry.minute}
                        onChange={e => {
                          const updated = [...sendNowEntries];
                          updated[index].minute = e.target.value;
                          setSendNowEntries(updated);
                        }}
                        style={{ width: '80px' }}
                      />
                    </div>
                    {sendNowEntries.length > 1 && (
                      <button
                        className="btn-secondary"
                        style={{ marginTop: '20px' }}
                        onClick={() =>
                          setSendNowEntries(
                            sendNowEntries.filter((_, i) => i !== index)
                          )
                        }
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <div
                  style={{ display: 'flex', gap: '12px', marginTop: '16px' }}
                >
                  <button
                    className="btn-secondary"
                    onClick={() =>
                      setSendNowEntries([
                        ...sendNowEntries,
                        { date: '', storyId: '', hour: '', minute: '' },
                      ])
                    }
                  >
                    + Add More
                  </button>
                  <button
                    className="btn-primary"
                    disabled={submittingSendNow}
                    onClick={async () => {
                      const valid = sendNowEntries.filter(
                        e => e.date && e.storyId
                      );
                      if (valid.length === 0) {
                        alert('Please fill in at least one entry.');
                        return;
                      }
                      setSubmittingSendNow(true);
                      try {
                        for (const entry of valid) {
                          const d = new Date(entry.date);
                          await authService.createConstant({
                            constant_name: 'manual story id',
                            constant_value: entry.storyId,
                            day: d.getDate(),
                            month: d.getMonth() + 1,
                            year: d.getFullYear(),
                            hour: parseInt(entry.hour, 10) || 0,
                            minute: parseInt(entry.minute, 10) || 0,
                            is_sent: false,
                            type: 'immediate',
                          });
                        }
                        alert(
                          `${valid.length} notification(s) sent successfully!`
                        );
                        setSendNowEntries([
                          { date: '', storyId: '', hour: '', minute: '' },
                        ]);
                        const data = await authService.getConstants();
                        setScheduledNotifications(data);
                      } catch (err) {
                        alert('Failed to send notifications.');
                      } finally {
                        setSubmittingSendNow(false);
                      }
                    }}
                  >
                    {submittingSendNow ? 'Sending...' : 'Send All'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: '32px' }}>
              <h3 style={{ marginBottom: '16px' }}>Scheduled Notifications</h3>
              {notificationsLoading ? (
                <p>Loading...</p>
              ) : scheduledNotifications.length === 0 ? (
                <p>No scheduled notifications found.</p>
              ) : (
                <div className="challenges-grid">
                  {scheduledNotifications
                    .filter(n => {
                      if (notificationAction === 'schedule')
                        return n.type === 'scheduled';
                      if (notificationAction === 'send-now')
                        return n.type === 'immediate';
                      return true;
                    })
                    .map(n => (
                      <div key={n.constant_id} className="challenge-card">
                        <div className="challenge-header">
                          <h3
                            className="challenge-title"
                            style={{ fontSize: '14px' }}
                          >
                            {n.constant_id}
                          </h3>
                          {(() => {
                            const parts = n.constant_id.split('_');
                            const day = parseInt(parts[0], 10);
                            const month = parseInt(parts[1], 10) - 1;
                            const year = parseInt(parts[2], 10);
                            const hr = parseInt(parts[3], 10);
                            const min = parseInt(parts[4], 10);
                            const hasTime = !isNaN(hr) && !isNaN(min);
                            const scheduledTime = hasTime
                              ? new Date(year, month, day, hr, min)
                              : new Date(year, month, day, 22, 0);
                            const now = new Date();
                            const istOffset = 5.5 * 60 * 60 * 1000;
                            const istNow = new Date(
                              now.getTime() +
                                istOffset +
                                now.getTimezoneOffset() * 60000
                            );
                            const sent = istNow >= scheduledTime;
                            return (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                  color: sent ? '#27ae60' : '#f39c12',
                                }}
                              >
                                {sent ? (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#27ae60"
                                    strokeWidth="2"
                                  >
                                    <path d="M20 6L9 17l-5-5" />
                                  </svg>
                                ) : (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#f39c12"
                                    strokeWidth="2"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M12 6v6l4 2" />
                                  </svg>
                                )}
                                {sent ? 'Sent' : 'Scheduled'}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="challenge-meta">
                          <span>
                            <strong>Story ID:</strong> {n.constant_value}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            marginTop: '12px',
                          }}
                        >
                          <button
                            className="btn-primary"
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                            onClick={async () => {
                              setStoryDataLoading(true);
                              try {
                                const token = localStorage.getItem('authToken');
                                const res = await fetch(
                                  `${config.API_BASE_URL}/story/${n.constant_value}`,
                                  {
                                    headers: {
                                      accept: 'application/json',
                                      ...(token && {
                                        'x-auth-token': token,
                                      }),
                                    },
                                  }
                                );
                                const data = await res.json();
                                setNotificationStoryForm(data);
                                setEditingNotificationStory({
                                  ...data,
                                  storyId: n.constant_value,
                                });
                              } catch {
                                alert('Failed to fetch story data');
                              } finally {
                                setStoryDataLoading(false);
                              }
                            }}
                          >
                            Edit Story
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <Navigation showBack={false} />
            <div className="content-header">
              <div className="header-content">
                <h1 className="page-title">Quest</h1>
                <p className="page-subtitle">Manage and edit your quests</p>
              </div>
              <button className="add-quest-button" onClick={handleAddQuest}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Quest
              </button>
            </div>

            {error && (
              <div className="error-message">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                {error}
              </div>
            )}

            <div className="quests-grid">
              {quests.length === 0 ? (
                <div className="empty-state">
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                  </svg>
                  <h3>No quests available</h3>
                  <p>No quests found in the system</p>
                </div>
              ) : (
                quests.map(quest => (
                  <div
                    key={quest.id}
                    className="quest-card"
                    onClick={() => handleQuestCardClick(quest.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="quest-image">
                      {quest.image_url ? (
                        <img src={quest.image_url} alt={quest.title} />
                      ) : (
                        <div className="quest-image-placeholder">
                          <svg
                            width="40"
                            height="40"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                            />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21,15 16,10 5,21" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="quest-content">
                      <h3 className="quest-title">{quest.title}</h3>
                      <p className="quest-subtitle">{quest.subtitle}</p>
                      <p className="quest-description">{quest.description}</p>
                      {(quest.created_at || quest.update_at) && (
                        <div className="quest-timestamps">
                          {quest.created_at && (
                            <span className="quest-timestamp">
                              Created{' '}
                              {new Date(quest.created_at).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                }
                              )}
                            </span>
                          )}
                          {quest.update_at && (
                            <span className="quest-timestamp">
                              Updated{' '}
                              {new Date(quest.update_at).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                }
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="quest-meta">
                        <div className="quest-info">
                          <span className="quest-duration">
                            {quest.duration} min
                          </span>
                          <span className="quest-modules">
                            {quest.no_of_modules} modules
                          </span>
                        </div>
                        <button
                          className="edit-quest-button"
                          onClick={e => {
                            e.stopPropagation();
                            handleEditQuest(quest);
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
      <QuestModal
        isOpen={showModal}
        onClose={handleModalClose}
        quest={editingQuest}
        onSave={handleQuestSave}
      />

      {/* Add Post Modal */}
      {showAddPostModal && (
        <div className="modal-overlay">
          <div className="modal-content add-post-modal">
            <div className="modal-header">
              <h2>{editingPost ? 'Edit Post' : 'Add New Post'}</h2>
              <button className="modal-close" onClick={handleCloseAddPostModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="post_title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="post_title"
                  value={postForm.post_title}
                  onChange={e =>
                    handlePostFormChange('post_title', e.target.value)
                  }
                  placeholder="Enter title"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="post_subtitle">Description</label>
                <textarea
                  id="post_subtitle"
                  value={postForm.post_subtitle}
                  onChange={e =>
                    handlePostFormChange('post_subtitle', e.target.value)
                  }
                  placeholder="Enter description"
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>URLs (optional)</label>
                {postForm.urls.map((url, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <input
                      type="url"
                      value={url}
                      onChange={e => {
                        const newUrls = [...postForm.urls];
                        newUrls[idx] = e.target.value;
                        handlePostFormChange('urls', newUrls);
                      }}
                      placeholder="https://example.com/media"
                      className="form-input"
                      style={{ flex: 1 }}
                    />
                    {postForm.urls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newUrls = postForm.urls.filter(
                            (_, i) => i !== idx
                          );
                          handlePostFormChange('urls', newUrls);
                        }}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() =>
                    handlePostFormChange('urls', [...postForm.urls, ''])
                  }
                  style={{ marginTop: '8px' }}
                >
                  + Add URL
                </button>
              </div>

              <div className="form-group">
                <label htmlFor="lrc_url">LRC URL (optional)</label>
                <input
                  type="url"
                  id="lrc_url"
                  value={postForm.lrc_url}
                  onChange={e =>
                    handlePostFormChange('lrc_url', e.target.value)
                  }
                  placeholder="https://example.com/lyrics.lrc"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="post_type">Post Type</label>
                <select
                  id="post_type"
                  value={postForm.post_type}
                  onChange={e =>
                    handlePostFormChange('post_type', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="image">Image</option>
                  <option value="text">Text</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="language">Language</label>
                <select
                  id="language"
                  value={postForm.language}
                  onChange={e =>
                    handlePostFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="quest_id">Entity ID (optional)</label>
                <select
                  id="quest_id"
                  value={postForm.quest_id}
                  onChange={e => handleQuestChange(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select entity</option>
                  <optgroup label="Daily Challenges">
                    {challenges.map(challenge => (
                      <option key={challenge.id} value={challenge.id}>
                        {challenge.title}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Mastery Challenges">
                    {masteryChallenges.map(challenge => (
                      <option key={challenge.id} value={challenge.id}>
                        {challenge.title}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <select
                  id="category"
                  value={postForm.category || ''}
                  onChange={e => {
                    handlePostFormChange('category', e.target.value);
                  }}
                  className="form-input"
                >
                  <option value="">Select a category</option>
                  {categories.map(slug => (
                    <option key={slug} value={slug}>
                      {categoryMap[slug]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tags</label>
                <div className="tag-selection-container">
                  <div className="tag-grid">
                    {tagsLoading ? (
                      <div className="tags-loading">
                        <div className="loading-spinner"></div>
                        <span>Loading tags...</span>
                      </div>
                    ) : availableTags.length > 0 ? (
                      availableTags.map(tag => (
                        <div
                          key={tag.id}
                          className={`tag-item ${postForm.tags.includes(tag.id) ? 'selected' : ''}`}
                          onClick={() => {
                            const isSelected = postForm.tags.includes(tag.id);
                            const updatedTags = isSelected
                              ? postForm.tags.filter(t => t !== tag.id)
                              : [...postForm.tags, tag.id];
                            handlePostFormChange('tags', updatedTags);
                          }}
                        >
                          <div className="tag-content">
                            <span className="tag-label">{tag.name}</span>
                            {postForm.tags.includes(tag.id) && (
                              <svg
                                className="tag-check-icon"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-tags-message">
                        <span>No tags available</span>
                      </div>
                    )}
                  </div>
                  {postForm.tags.length > 0 && (
                    <div className="selected-tags-preview">
                      <span className="selected-count">
                        {postForm.tags.length} tag
                        {postForm.tags.length !== 1 ? 's' : ''} selected
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseAddPostModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitPost}
                disabled={submittingPost}
              >
                {submittingPost ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingPost ? 'Updating Post...' : 'Creating Post...'}
                  </>
                ) : editingPost ? (
                  'Update Post'
                ) : (
                  'Create Post'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Challenge Modal */}
      {showAddChallengeModal && (
        <div className="modal-overlay">
          <div className="modal-content add-challenge-modal">
            <div className="modal-header">
              <h2>
                {editingChallenge ? 'Edit Challenge' : 'Add New Challenge'}
              </h2>
              <button
                className="modal-close"
                onClick={handleCloseAddChallengeModal}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="challenge_title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="challenge_title"
                  value={challengeForm.title}
                  onChange={e =>
                    handleChallengeFormChange('title', e.target.value)
                  }
                  placeholder="Enter challenge title"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_subtitle">Subtitle</label>
                <input
                  type="text"
                  id="challenge_subtitle"
                  value={challengeForm.subtitle}
                  onChange={e =>
                    handleChallengeFormChange('subtitle', e.target.value)
                  }
                  placeholder="Enter challenge subtitle"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_ai_coach_context">
                  AI Coach Context
                </label>
                <textarea
                  id="challenge_ai_coach_context"
                  value={challengeForm.ai_coach_context}
                  onChange={e =>
                    handleChallengeFormChange(
                      'ai_coach_context',
                      e.target.value
                    )
                  }
                  placeholder="Enter AI coach context"
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_icon_url">Icon URL</label>
                <input
                  type="url"
                  id="challenge_icon_url"
                  value={challengeForm.icon_url}
                  onChange={e =>
                    handleChallengeFormChange('icon_url', e.target.value)
                  }
                  placeholder="https://example.com/icon.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_image_url">Image URL</label>
                <input
                  type="url"
                  id="challenge_image_url"
                  value={challengeForm.image_url}
                  onChange={e =>
                    handleChallengeFormChange('image_url', e.target.value)
                  }
                  placeholder="https://example.com/image.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_emoji">Emoji</label>
                <input
                  type="text"
                  id="challenge_emoji"
                  value={challengeForm.emoji}
                  onChange={e =>
                    handleChallengeFormChange('emoji', e.target.value)
                  }
                  placeholder="Enter emoji (e.g., 🎯)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_language">Language</label>
                <select
                  id="challenge_language"
                  value={challengeForm.language}
                  onChange={e =>
                    handleChallengeFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="challenge_share_text">Share Text</label>
                <input
                  type="text"
                  id="challenge_share_text"
                  value={challengeForm.share_text}
                  onChange={e =>
                    handleChallengeFormChange('share_text', e.target.value)
                  }
                  placeholder="Enter share text"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_share_image_url">
                  Share Image URL
                </label>
                <input
                  type="url"
                  id="challenge_share_image_url"
                  value={challengeForm.share_image_url}
                  onChange={e =>
                    handleChallengeFormChange('share_image_url', e.target.value)
                  }
                  placeholder="https://example.com/share-image.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_category">Category</label>
                <select
                  id="challenge_category"
                  value={challengeForm.category || ''}
                  onChange={e =>
                    handleChallengeFormChange('category', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="">Select a category</option>
                  {filteredCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tags</label>
                {filteredTags.length > 0 ? (
                  <div className="tag-selection-container">
                    <div className="tag-grid">
                      {filteredTags.map(tag => (
                        <div
                          key={tag.id}
                          className={`tag-item ${
                            challengeForm.tags.includes(tag.id)
                              ? 'selected'
                              : ''
                          }`}
                          onClick={() => {
                            const isSelected = challengeForm.tags.includes(
                              tag.id
                            );
                            const updatedTags = isSelected
                              ? challengeForm.tags.filter(t => t !== tag.id)
                              : [...challengeForm.tags, tag.id];
                            handleChallengeFormChange('tags', updatedTags);
                          }}
                        >
                          <div className="tag-content">
                            <span className="tag-label">{tag.name}</span>
                            {challengeForm.tags.includes(tag.id) && (
                              <svg
                                className="tag-check-icon"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {challengeForm.tags.length > 0 && (
                      <div className="selected-tags-preview">
                        <span className="selected-count">
                          {challengeForm.tags.length} tag
                          {challengeForm.tags.length !== 1 ? 's' : ''} selected
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="form-input disabled-input">
                    No tags available for this language
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="challenge_start_color">Start Color</label>
                <input
                  type="text"
                  id="challenge_start_color"
                  value={challengeForm.start_color}
                  onChange={e =>
                    handleChallengeFormChange('start_color', e.target.value)
                  }
                  placeholder="#FFFFFF"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_max_bucket">Max Bucket</label>
                <input
                  type="text"
                  id="challenge_max_bucket"
                  value={challengeForm.max_bucket}
                  onChange={e =>
                    handleChallengeFormChange('max_bucket', e.target.value)
                  }
                  placeholder="100"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="challenge_min_bucket">Min Bucket</label>
                <input
                  type="text"
                  id="challenge_min_bucket"
                  value={challengeForm.min_bucket}
                  onChange={e =>
                    handleChallengeFormChange('min_bucket', e.target.value)
                  }
                  placeholder="0"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={challengeForm.is_public}
                    onChange={e =>
                      handleChallengeFormChange('is_public', e.target.checked)
                    }
                    className="form-checkbox"
                  />
                  <span>Make this challenge public</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseAddChallengeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitChallenge}
                disabled={submittingChallenge}
              >
                {submittingChallenge ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingChallenge ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingChallenge ? (
                  'Update Challenge'
                ) : (
                  'Create Challenge'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Mastery Challenge Modal */}
      {showAddMasteryChallengeModal && (
        <div className="modal-overlay">
          <div className="modal-content add-challenge-modal">
            <div className="modal-header">
              <h2>
                {editingMasteryChallenge
                  ? 'Edit Mastery Challenge'
                  : 'Add New Mastery Challenge'}
              </h2>
              <button
                className="modal-close"
                onClick={handleCloseAddMasteryChallengeModal}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="mastery_title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="mastery_title"
                  value={masteryChallengeForm.title}
                  onChange={e =>
                    handleMasteryChallengeFormChange('title', e.target.value)
                  }
                  placeholder="Enter challenge title"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_subtitle">Subtitle</label>
                <input
                  type="text"
                  id="mastery_subtitle"
                  value={masteryChallengeForm.subtitle}
                  onChange={e =>
                    handleMasteryChallengeFormChange('subtitle', e.target.value)
                  }
                  placeholder="Enter challenge subtitle"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_ai_coach_context">
                  AI Coach Context
                </label>
                <textarea
                  id="mastery_ai_coach_context"
                  value={masteryChallengeForm.ai_coach_context}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'ai_coach_context',
                      e.target.value
                    )
                  }
                  placeholder="Enter AI coach context"
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_icon_url">Icon URL</label>
                <input
                  type="url"
                  id="mastery_icon_url"
                  value={masteryChallengeForm.icon_url}
                  onChange={e =>
                    handleMasteryChallengeFormChange('icon_url', e.target.value)
                  }
                  placeholder="https://example.com/icon.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_image_url">Image URL</label>
                <input
                  type="url"
                  id="mastery_image_url"
                  value={masteryChallengeForm.image_url}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'image_url',
                      e.target.value
                    )
                  }
                  placeholder="https://example.com/image.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_emoji">Emoji</label>
                <input
                  type="text"
                  id="mastery_emoji"
                  value={masteryChallengeForm.emoji}
                  onChange={e =>
                    handleMasteryChallengeFormChange('emoji', e.target.value)
                  }
                  placeholder="Enter emoji (e.g., ⭐)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_language">Language</label>
                <select
                  id="mastery_language"
                  value={masteryChallengeForm.language}
                  onChange={e =>
                    handleMasteryChallengeFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="mastery_share_text">Share Text</label>
                <input
                  type="text"
                  id="mastery_share_text"
                  value={masteryChallengeForm.share_text}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'share_text',
                      e.target.value
                    )
                  }
                  placeholder="Enter share text"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_share_image_url">Share Image URL</label>
                <input
                  type="url"
                  id="mastery_share_image_url"
                  value={masteryChallengeForm.share_image_url}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'share_image_url',
                      e.target.value
                    )
                  }
                  placeholder="https://example.com/share-image.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_category">Category</label>
                <select
                  id="mastery_category"
                  value={masteryChallengeForm.category || ''}
                  onChange={e =>
                    handleMasteryChallengeFormChange('category', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="">Select a category</option>
                  {filteredMasteryCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="l0_category">L0 Category</label>
                <select
                  id="l0_category"
                  value={masteryChallengeForm.l0_category || ''}
                  onChange={e => {
                    handleMasteryChallengeFormChange(
                      'l0_category',
                      e.target.value
                    );
                    handleMasteryChallengeFormChange('l1_category', '');
                    handleMasteryChallengeFormChange('l2_category', '');
                  }}
                  className="form-input"
                >
                  <option value="">Select L0 category</option>
                  {l0Categories.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="l1_category">L1 Category</label>
                <select
                  id="l1_category"
                  value={masteryChallengeForm.l1_category || ''}
                  onChange={e => {
                    handleMasteryChallengeFormChange(
                      'l1_category',
                      e.target.value
                    );
                    handleMasteryChallengeFormChange('l2_category', '');
                  }}
                  className="form-input"
                  disabled={!masteryChallengeForm.l0_category}
                >
                  <option value="">Select L1 category</option>
                  {l1Categories
                    .filter(
                      (cat: any) =>
                        cat.parent_category === masteryChallengeForm.l0_category
                    )
                    .map((cat: any) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="l2_category">L2 Category</label>
                <select
                  id="l2_category"
                  value={masteryChallengeForm.l2_category || ''}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'l2_category',
                      e.target.value
                    )
                  }
                  className="form-input"
                  disabled={!masteryChallengeForm.l1_category}
                >
                  <option value="">Select L2 category</option>
                  {l2Categories
                    .filter(
                      (cat: any) =>
                        cat.parent_category === masteryChallengeForm.l1_category
                    )
                    .map((cat: any) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tags</label>
                {filteredMasteryTags.length > 0 ? (
                  <div className="tag-selection-container">
                    <div className="tag-grid">
                      {filteredMasteryTags.map(tag => (
                        <div
                          key={tag.id}
                          className={`tag-item ${
                            masteryChallengeForm.tags.includes(tag.id)
                              ? 'selected'
                              : ''
                          }`}
                          onClick={() => {
                            const isSelected =
                              masteryChallengeForm.tags.includes(tag.id);
                            const updatedTags = isSelected
                              ? masteryChallengeForm.tags.filter(
                                  t => t !== tag.id
                                )
                              : [...masteryChallengeForm.tags, tag.id];
                            handleMasteryChallengeFormChange(
                              'tags',
                              updatedTags
                            );
                          }}
                        >
                          <div className="tag-content">
                            <span className="tag-label">{tag.name}</span>
                            {masteryChallengeForm.tags.includes(tag.id) && (
                              <svg
                                className="tag-check-icon"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {masteryChallengeForm.tags.length > 0 && (
                      <div className="selected-tags-preview">
                        <span className="selected-count">
                          {masteryChallengeForm.tags.length} tag
                          {masteryChallengeForm.tags.length !== 1
                            ? 's'
                            : ''}{' '}
                          selected
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="form-input disabled-input">
                    No tags available for this language
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="mastery_start_color">Start Color</label>
                <input
                  type="text"
                  id="mastery_start_color"
                  value={masteryChallengeForm.start_color}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'start_color',
                      e.target.value
                    )
                  }
                  placeholder="#FFFFFF"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_max_bucket">Max Bucket</label>
                <input
                  type="text"
                  id="mastery_max_bucket"
                  value={masteryChallengeForm.max_bucket}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'max_bucket',
                      e.target.value
                    )
                  }
                  placeholder="100"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mastery_min_bucket">Min Bucket</label>
                <input
                  type="text"
                  id="mastery_min_bucket"
                  value={masteryChallengeForm.min_bucket}
                  onChange={e =>
                    handleMasteryChallengeFormChange(
                      'min_bucket',
                      e.target.value
                    )
                  }
                  placeholder="0"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={masteryChallengeForm.is_public}
                    onChange={e =>
                      handleMasteryChallengeFormChange(
                        'is_public',
                        e.target.checked
                      )
                    }
                    className="form-checkbox"
                  />
                  <span>Make this challenge public</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseAddMasteryChallengeModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitMasteryChallenge}
                disabled={submittingMasteryChallenge}
              >
                {submittingMasteryChallenge ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingMasteryChallenge ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingMasteryChallenge ? (
                  'Update Challenge'
                ) : (
                  'Create Challenge'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Audio Modal */}
      {showAddAudioModal && (
        <div className="modal-overlay">
          <div className="modal-content add-audio-modal">
            <div className="modal-header">
              <h2>Add Audio URL</h2>
              <button
                className="modal-close"
                onClick={() => setShowAddAudioModal(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Language</label>
                <select
                  value={audioForm.language}
                  onChange={e =>
                    handleAudioFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="">Select Language</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>
              <div className="form-group">
                <label>Purpose</label>
                <input
                  type="text"
                  value={audioForm.purpose}
                  onChange={e =>
                    handleAudioFormChange('purpose', e.target.value)
                  }
                  placeholder="e.g., Meditation, Motivation"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Audio URLs</label>
                {audioForm.audio_urls.map((url, idx) => (
                  <input
                    key={idx}
                    type="url"
                    value={url}
                    onChange={e => handleAudioUrlChange(idx, e.target.value)}
                    placeholder="https://example.com/audio.mp3"
                    className="form-input"
                    style={{ marginBottom: '8px' }}
                  />
                ))}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleAddAudioUrlField}
                >
                  + Add URL
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowAddAudioModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmitAudio}
                disabled={submittingAudio}
              >
                {submittingAudio ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tag Modal */}
      {showAddTagModal && (
        <div className="modal-overlay">
          <div className="modal-content add-tag-modal">
            <div className="modal-header">
              <h2>{editingTag ? 'Edit Tag' : 'Add New Tag'}</h2>
              <button className="modal-close" onClick={handleCloseAddTagModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="tag_name">Tag Name</label>
                <input
                  type="text"
                  id="tag_name"
                  value={tagForm.name}
                  onChange={e => handleTagFormChange('name', e.target.value)}
                  placeholder="Enter tag name (e.g., Technology, Health)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tag_image_url">Image URL</label>
                <input
                  type="url"
                  id="tag_image_url"
                  value={tagForm.image_url}
                  onChange={e =>
                    handleTagFormChange('image_url', e.target.value)
                  }
                  placeholder="https://example.com/tag-image.jpg"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tag_text_color">Text Color</label>
                <input
                  type="text"
                  id="tag_text_color"
                  value={tagForm.text_color}
                  onChange={e =>
                    handleTagFormChange('text_color', e.target.value)
                  }
                  placeholder="FF713F12 (without #)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tag_language">Language</label>
                <select
                  id="tag_language"
                  value={tagForm.language}
                  onChange={e =>
                    handleTagFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="hinglish">Hinglish</option>
                  <option value="hindi">Hindi</option>
                  <option value="english">English</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="tag_version">Version</label>
                <select
                  id="tag_version"
                  value={tagForm.version}
                  onChange={e => handleTagFormChange('version', e.target.value)}
                  className="form-input"
                >
                  <option value="v1">v1</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseAddTagModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitTag}
                disabled={submittingTag}
              >
                {submittingTag ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingTag ? 'Updating Tag...' : 'Creating Tag...'}
                  </>
                ) : editingTag ? (
                  'Update Tag'
                ) : (
                  'Create Tag'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Coach Modal */}
      {showAddCoachModal && (
        <div className="modal-overlay">
          <div className="modal-content add-challenge-modal">
            <div className="modal-header">
              <h2>{editingCoach ? 'Edit Coach' : 'Add New Coach'}</h2>
              <button className="modal-close" onClick={handleCloseCoachModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="coach_name">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="coach_name"
                  value={coachForm.name}
                  onChange={e => handleCoachFormChange('name', e.target.value)}
                  placeholder="Enter coach name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_tagline">Tagline</label>
                <input
                  type="text"
                  id="coach_tagline"
                  value={coachForm.tagline}
                  onChange={e =>
                    handleCoachFormChange('tagline', e.target.value)
                  }
                  placeholder="Enter coach tagline"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_description">Description</label>
                <textarea
                  id="coach_description"
                  value={coachForm.description}
                  onChange={e =>
                    handleCoachFormChange('description', e.target.value)
                  }
                  placeholder="Enter coach description"
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_greeting">Greeting</label>
                <textarea
                  id="coach_greeting"
                  value={coachForm.greeting}
                  onChange={e =>
                    handleCoachFormChange('greeting', e.target.value)
                  }
                  placeholder="Enter coach greeting message"
                  className="form-input"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_introduction">Introduction</label>
                <textarea
                  id="coach_introduction"
                  value={coachForm.introduction}
                  onChange={e =>
                    handleCoachFormChange('introduction', e.target.value)
                  }
                  placeholder="Enter coach introduction"
                  className="form-input"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_avatar_url">Avatar Image URL</label>
                <input
                  type="url"
                  id="coach_avatar_url"
                  value={coachForm.avatar_image_url}
                  onChange={e =>
                    handleCoachFormChange('avatar_image_url', e.target.value)
                  }
                  placeholder="https://example.com/avatar.jpg"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_banner_url">Banner Image URL</label>
                <input
                  type="url"
                  id="coach_banner_url"
                  value={coachForm.banner_image_url}
                  onChange={e =>
                    handleCoachFormChange('banner_image_url', e.target.value)
                  }
                  placeholder="https://example.com/banner.jpg"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_gender">Gender</label>
                <input
                  type="text"
                  id="coach_gender"
                  value={coachForm.gender}
                  onChange={e =>
                    handleCoachFormChange('gender', e.target.value)
                  }
                  placeholder="Enter gender"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_model_name">Model Name</label>
                <input
                  type="text"
                  id="coach_model_name"
                  value={coachForm.model_name}
                  onChange={e =>
                    handleCoachFormChange('model_name', e.target.value)
                  }
                  placeholder="gpt-3.5-turbo"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_app_name">App Name</label>
                <input
                  type="text"
                  id="coach_app_name"
                  value={coachForm.app_name}
                  onChange={e =>
                    handleCoachFormChange('app_name', e.target.value)
                  }
                  placeholder="Quests App"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_language">Language</label>
                <select
                  id="coach_language"
                  value={coachForm.language}
                  onChange={e =>
                    handleCoachFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="coach_first_question">First Question</label>
                <textarea
                  id="coach_first_question"
                  value={coachForm.first_question}
                  onChange={e =>
                    handleCoachFormChange('first_question', e.target.value)
                  }
                  placeholder="Enter first question"
                  className="form-input"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_second_question">Second Question</label>
                <textarea
                  id="coach_second_question"
                  value={coachForm.second_question}
                  onChange={e =>
                    handleCoachFormChange('second_question', e.target.value)
                  }
                  placeholder="Enter second question"
                  className="form-input"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_welcome_message">Welcome Message</label>
                <textarea
                  id="coach_welcome_message"
                  value={coachForm.welcome_message}
                  onChange={e =>
                    handleCoachFormChange('welcome_message', e.target.value)
                  }
                  placeholder="Enter welcome message"
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_welcome_system_prompt">
                  Welcome System Prompt
                </label>
                <textarea
                  id="coach_welcome_system_prompt"
                  value={coachForm.welcome_system_prompt}
                  onChange={e =>
                    handleCoachFormChange(
                      'welcome_system_prompt',
                      e.target.value
                    )
                  }
                  placeholder="Enter welcome system prompt"
                  className="form-input"
                  rows={4}
                />
              </div>

              {/* Welcome Experiment Section */}
              <div className="form-group">
                <label>Welcome Experiment</label>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleAddWelcomeExperiment}
                  style={{
                    backgroundColor: '#28a745',
                    borderColor: '#28a745',
                    color: 'white',
                    marginTop: '10px',
                  }}
                >
                  + Add Welcome Experiment
                </button>
              </div>

              {welcomeExperiments.map((experiment, index) => (
                <div
                  key={index}
                  style={{
                    border: '1px solid #ddd',
                    padding: '15px',
                    marginBottom: '15px',
                    borderRadius: '5px',
                    position: 'relative',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleRemoveWelcomeExperiment(index)}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      backgroundColor: '#dc3545',
                      borderColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      padding: '5px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                  <div className="form-group">
                    <label>Prompt</label>
                    <textarea
                      className="form-input"
                      value={experiment.prompt}
                      onChange={e =>
                        handleWelcomeExperimentChange(
                          index,
                          'prompt',
                          e.target.value
                        )
                      }
                      placeholder="Enter prompt"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      className="form-input"
                      value={experiment.message}
                      onChange={e =>
                        handleWelcomeExperimentChange(
                          index,
                          'message',
                          e.target.value
                        )
                      }
                      placeholder="Enter message"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Bucket List (comma-separated numbers)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={experiment.bucket_list}
                      onChange={e =>
                        handleWelcomeExperimentChange(
                          index,
                          'bucket_list',
                          e.target.value
                        )
                      }
                      placeholder="e.g., 1, 2, 3"
                    />
                  </div>
                </div>
              ))}

              <div className="form-group">
                <label htmlFor="coach_temperature">Temperature</label>
                <input
                  type="number"
                  id="coach_temperature"
                  value={coachForm.temperature}
                  onChange={e =>
                    handleCoachFormChange(
                      'temperature',
                      parseFloat(e.target.value)
                    )
                  }
                  min="0"
                  max="2"
                  step="0.1"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_max_tokens">Max Tokens</label>
                <input
                  type="number"
                  id="coach_max_tokens"
                  value={coachForm.max_tokens}
                  onChange={e =>
                    handleCoachFormChange(
                      'max_tokens',
                      parseInt(e.target.value)
                    )
                  }
                  min="1"
                  max="4000"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_top_p">Top P</label>
                <input
                  type="number"
                  id="coach_top_p"
                  value={coachForm.top_p}
                  onChange={e =>
                    handleCoachFormChange('top_p', parseFloat(e.target.value))
                  }
                  min="0"
                  max="1"
                  step="0.1"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_top_k">Top K</label>
                <input
                  type="number"
                  id="coach_top_k"
                  value={coachForm.top_k}
                  onChange={e =>
                    handleCoachFormChange('top_k', parseInt(e.target.value))
                  }
                  min="0"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_top_a">Top A</label>
                <input
                  type="number"
                  id="coach_top_a"
                  value={coachForm.top_a}
                  onChange={e =>
                    handleCoachFormChange('top_a', parseFloat(e.target.value))
                  }
                  min="0"
                  max="1"
                  step="0.1"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_min_p">Min P</label>
                <input
                  type="number"
                  id="coach_min_p"
                  value={coachForm.min_p}
                  onChange={e =>
                    handleCoachFormChange('min_p', parseFloat(e.target.value))
                  }
                  min="0"
                  max="1"
                  step="0.1"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_frequency_penalty">
                  Frequency Penalty
                </label>
                <input
                  type="number"
                  id="coach_frequency_penalty"
                  value={coachForm.frequency_penalty}
                  onChange={e =>
                    handleCoachFormChange(
                      'frequency_penalty',
                      parseFloat(e.target.value)
                    )
                  }
                  min="-2"
                  max="2"
                  step="0.1"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_presence_penalty">Presence Penalty</label>
                <input
                  type="number"
                  id="coach_presence_penalty"
                  value={coachForm.presence_penalty}
                  onChange={e =>
                    handleCoachFormChange(
                      'presence_penalty',
                      parseFloat(e.target.value)
                    )
                  }
                  min="-2"
                  max="2"
                  step="0.1"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_repetition_penalty">
                  Repetition Penalty
                </label>
                <input
                  type="number"
                  id="coach_repetition_penalty"
                  value={coachForm.repetition_penalty}
                  onChange={e =>
                    handleCoachFormChange(
                      'repetition_penalty',
                      parseFloat(e.target.value)
                    )
                  }
                  min="0"
                  max="2"
                  step="0.1"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="coach_visibility">Visibility</label>
                <select
                  id="coach_visibility"
                  value={coachForm.visibility}
                  onChange={e =>
                    handleCoachFormChange('visibility', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="PUBLIC">Public</option>
                  <option value="PRIVATE">Private</option>
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={coachForm.nsfw}
                    onChange={e =>
                      handleCoachFormChange('nsfw', e.target.checked)
                    }
                    className="form-checkbox"
                  />
                  <span>NSFW Content</span>
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={coachForm.active}
                    onChange={e =>
                      handleCoachFormChange('active', e.target.checked)
                    }
                    className="form-checkbox"
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="form-group">
                <label>Range Prompts</label>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleAddRangePrompt}
                  style={{
                    marginBottom: '10px',
                    backgroundColor: '#28a745',
                    borderColor: '#28a745',
                    color: 'white',
                  }}
                >
                  Add Range Prompt
                </button>
                {rangePrompts.map((rangePrompt, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: '10px',
                      marginBottom: '10px',
                    }}
                  >
                    <div style={{ flex: '0 0 80px' }}>
                      <label>Min</label>
                      <input
                        type="text"
                        className="form-input"
                        value={rangePrompt.min}
                        onChange={e =>
                          handleRangePromptChange(index, 'min', e.target.value)
                        }
                        placeholder="Min"
                      />
                    </div>
                    <div style={{ flex: '0 0 80px' }}>
                      <label>Max</label>
                      <input
                        type="text"
                        className="form-input"
                        value={rangePrompt.max}
                        onChange={e =>
                          handleRangePromptChange(index, 'max', e.target.value)
                        }
                        placeholder="Max"
                      />
                    </div>
                    <div style={{ flex: '1' }}>
                      <label>Prompt</label>
                      <textarea
                        className="form-input"
                        value={rangePrompt.prompt}
                        onChange={e =>
                          handleRangePromptChange(
                            index,
                            'prompt',
                            e.target.value
                          )
                        }
                        placeholder="Enter prompt text"
                        rows={3}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => handleRemoveRangePrompt(index)}
                      style={{
                        marginBottom: '0',
                        height: 'fit-content',
                        backgroundColor: '#dc3545',
                        borderColor: '#dc3545',
                        color: 'white',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseCoachModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitCoach}
                disabled={submittingCoach}
              >
                {submittingCoach ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingCoach ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingCoach ? (
                  'Update Coach'
                ) : (
                  'Create Coach'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="modal-overlay">
          <div className="modal-content add-tag-modal">
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
              <button
                className="modal-close"
                onClick={handleCloseCategoryModal}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && error !== 'validation' && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="category_name">
                  Category Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="category_name"
                  value={categoryForm.name}
                  onChange={e =>
                    handleCategoryFormChange('name', e.target.value)
                  }
                  placeholder="Enter category name"
                  className="form-input"
                />
                {error === 'validation' && !categoryForm.name.trim() && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    Category name is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="display_name">Display Name</label>
                <input
                  type="text"
                  id="display_name"
                  value={categoryForm.display_name}
                  onChange={e =>
                    handleCategoryFormChange('display_name', e.target.value)
                  }
                  placeholder="Enter display name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="text_color">Text Color</label>
                <input
                  type="text"
                  id="text_color"
                  value={categoryForm.text_color}
                  onChange={e =>
                    handleCategoryFormChange('text_color', e.target.value)
                  }
                  placeholder="Enter text color (e.g., #000000)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="image_url">Image URL</label>
                <input
                  type="url"
                  id="image_url"
                  value={categoryForm.image_url}
                  onChange={e =>
                    handleCategoryFormChange('image_url', e.target.value)
                  }
                  placeholder="Enter image URL"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="emoji">Emoji</label>
                <input
                  type="text"
                  id="emoji"
                  value={categoryForm.emoji}
                  onChange={e =>
                    handleCategoryFormChange('emoji', e.target.value)
                  }
                  placeholder="Enter emoji (e.g., 📁)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="parent_category">Parent Category</label>
                <select
                  id="parent_category"
                  value={categoryForm.parent_category}
                  onChange={e =>
                    handleCategoryFormChange('parent_category', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="">Select parent category</option>
                  {categoryList.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="level">
                  Level <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="level"
                  value={categoryForm.level}
                  onChange={e =>
                    handleCategoryFormChange('level', e.target.value)
                  }
                  placeholder="Enter level"
                  className="form-input"
                />
                {error === 'validation' && !categoryForm.level.trim() && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    Level is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="category_language">Language</label>
                <select
                  id="category_language"
                  value={categoryForm.language}
                  onChange={e =>
                    handleCategoryFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                  <option value="english">English</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="version">Version</label>
                <select
                  id="version"
                  value={categoryForm.version}
                  onChange={e =>
                    handleCategoryFormChange('version', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="v1">v1</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="rank">Rank</label>
                <input
                  type="number"
                  id="rank"
                  value={categoryForm.rank}
                  onChange={e =>
                    handleCategoryFormChange(
                      'rank',
                      parseInt(e.target.value) || 0
                    )
                  }
                  placeholder="Enter rank (e.g., 1, 2, 3)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={categoryForm.onboarding_flag}
                    onChange={e =>
                      handleCategoryFormChange(
                        'onboarding_flag',
                        e.target.checked
                      )
                    }
                    className="form-checkbox"
                  />
                  <span>Onboarding</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseCategoryModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitCategory}
                disabled={submittingCategory}
              >
                {submittingCategory ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingCategory ? 'Updating...' : 'Creating Category...'}
                  </>
                ) : editingCategory ? (
                  'Update Category'
                ) : (
                  'Create Category'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Experiment Module Modal */}
      {showAddExperimentModuleModal && (
        <div className="modal-overlay">
          <div className="modal-content add-challenge-modal">
            <div className="modal-header">
              <h2>
                {editingExperimentModule
                  ? 'Edit Experiment Module'
                  : 'Add New Experiment Module'}
              </h2>
              <button
                className="modal-close"
                onClick={handleCloseExperimentModuleModal}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="experiment_title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="experiment_title"
                  value={experimentModuleForm.title}
                  onChange={e =>
                    handleExperimentModuleFormChange('title', e.target.value)
                  }
                  placeholder="Enter module title"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_link_url">Link URL</label>
                <input
                  type="url"
                  id="experiment_link_url"
                  value={experimentModuleForm.link_url}
                  onChange={e =>
                    handleExperimentModuleFormChange('link_url', e.target.value)
                  }
                  placeholder="https://example.com/link"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_medal_title">Medal Title</label>
                <input
                  type="text"
                  id="experiment_medal_title"
                  value={experimentModuleForm.medal_title}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'medal_title',
                      e.target.value
                    )
                  }
                  placeholder="Enter medal title"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_medal_subtitle">
                  Medal Subtitle
                </label>
                <input
                  type="text"
                  id="experiment_medal_subtitle"
                  value={experimentModuleForm.medal_subtitle}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'medal_subtitle',
                      e.target.value
                    )
                  }
                  placeholder="Enter medal subtitle"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_medal_image_url">
                  Medal Image URL
                </label>
                <input
                  type="url"
                  id="experiment_medal_image_url"
                  value={experimentModuleForm.medal_image_url}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'medal_image_url',
                      e.target.value
                    )
                  }
                  placeholder="https://example.com/medal.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_medal_share_image_url">
                  Medal Share Image URL
                </label>
                <input
                  type="url"
                  id="experiment_medal_share_image_url"
                  value={experimentModuleForm.medal_share_image_url}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'medal_share_image_url',
                      e.target.value
                    )
                  }
                  placeholder="https://example.com/share.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_medal_joy_audio_url">
                  Medal Joy Audio URL
                </label>
                <input
                  type="url"
                  id="experiment_medal_joy_audio_url"
                  value={experimentModuleForm.medal_joy_audio_url}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'medal_joy_audio_url',
                      e.target.value
                    )
                  }
                  placeholder="https://example.com/audio.mp3"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_medal_share_text">
                  Medal Share Text
                </label>
                <input
                  type="text"
                  id="experiment_medal_share_text"
                  value={experimentModuleForm.medal_share_text}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'medal_share_text',
                      e.target.value
                    )
                  }
                  placeholder="Enter share text"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_medal_share_url">
                  Medal Share URL
                </label>
                <input
                  type="url"
                  id="experiment_medal_share_url"
                  value={experimentModuleForm.medal_share_url}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'medal_share_url',
                      e.target.value
                    )
                  }
                  placeholder="https://example.com/share"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_image_share_url">
                  Image Share URL
                </label>
                <input
                  type="url"
                  id="experiment_image_share_url"
                  value={experimentModuleForm.image_share_url}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'image_share_url',
                      e.target.value
                    )
                  }
                  placeholder="https://example.com/image.png"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_ai_coach_context">
                  AI Coach Context
                </label>
                <textarea
                  id="experiment_ai_coach_context"
                  value={experimentModuleForm.ai_coach_context}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'ai_coach_context',
                      e.target.value
                    )
                  }
                  placeholder="Enter AI coach context"
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_min_bucket">Min Bucket</label>
                <input
                  type="number"
                  id="experiment_min_bucket"
                  value={experimentModuleForm.min_bucket}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'min_bucket',
                      parseInt(e.target.value) || 0
                    )
                  }
                  placeholder="0"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_max_bucket">Max Bucket</label>
                <input
                  type="number"
                  id="experiment_max_bucket"
                  value={experimentModuleForm.max_bucket}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'max_bucket',
                      parseInt(e.target.value) || 0
                    )
                  }
                  placeholder="100"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="experiment_total_available_points">
                  Total Available Points
                </label>
                <input
                  type="number"
                  id="experiment_total_available_points"
                  value={experimentModuleForm.total_available_points}
                  onChange={e =>
                    handleExperimentModuleFormChange(
                      'total_available_points',
                      parseInt(e.target.value) || 0
                    )
                  }
                  placeholder="0"
                  className="form-input"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseExperimentModuleModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitExperimentModule}
                disabled={submittingExperimentModule}
              >
                {submittingExperimentModule ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingExperimentModule ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingExperimentModule ? (
                  'Update Module'
                ) : (
                  'Create Module'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowConfirmModal(false)}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Upload</h2>
              <button
                className="modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to upload{' '}
                <strong>{selectedFile?.name}</strong>?
              </p>
              <p>This will process the Excel file and create posts.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleConfirmUpload}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadingExcel && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div
              className="modal-body"
              style={{ textAlign: 'center', padding: '40px' }}
            >
              <div
                className="button-spinner"
                style={{ margin: '0 auto 20px' }}
              ></div>
              <h3>Uploading Excel File...</h3>
              <p>Please wait while we process your file.</p>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowUploadModal(false)}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Result</h2>
              <button
                className="modal-close"
                onClick={() => setShowUploadModal(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {uploadResult?.error ? (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {uploadResult.error}
                </div>
              ) : (
                <div className="upload-success">
                  <div className="success-header">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22,4 12,14.01 9,11.01" />
                    </svg>
                    <h3>Upload Successful!</h3>
                  </div>
                  <div className="result-content">
                    <pre>{JSON.stringify(uploadResult, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setShowUploadModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Story Modal */}
      {showAddStoryModal && (
        <div className="modal-overlay">
          <div className="modal-content add-challenge-modal">
            <div className="modal-header">
              <h2>{editingStory ? 'Edit Story' : 'Add New Story'}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddStoryModal(false);
                  setEditingStory(null);
                  setError(null);
                  setStoryForm({
                    title: '',
                    l0_category: '',
                    l1_category: '',
                    l2_category: '',
                    tags: [],
                    author: profile?.id || user?.id || '',
                    duration: 5,
                    language: '',
                    image_url: '',
                    is_active: true,
                    type: '',
                    content_life_in_days: 7,
                    script: '',
                  });
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && error !== 'validation' && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="story_title">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="story_title"
                  value={storyForm.title}
                  onChange={e => handleStoryFormChange('title', e.target.value)}
                  placeholder="Enter story title"
                  className="form-input"
                />
                {error === 'validation' && !storyForm.title.trim() && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    Title is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="story_language">Language</label>
                <select
                  id="story_language"
                  value={storyForm.language}
                  onChange={e =>
                    handleStoryFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="">Select language</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="story_l0_category">
                  L0 Category <span className="required">*</span>
                </label>
                <select
                  id="story_l0_category"
                  value={storyForm.l0_category}
                  onChange={e =>
                    handleStoryFormChange('l0_category', e.target.value)
                  }
                  className="form-input"
                  disabled={!storyForm.language}
                >
                  <option value="">Select L0 category</option>
                  {l0Categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {error === 'validation' && !storyForm.l0_category.trim() && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    L0 Category is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="story_l1_category">
                  L1 Category <span className="required">*</span>
                </label>
                <select
                  id="story_l1_category"
                  value={storyForm.l1_category}
                  onChange={e =>
                    handleStoryFormChange('l1_category', e.target.value)
                  }
                  className="form-input"
                  disabled={!storyForm.l0_category}
                >
                  <option value="">Select L1 category</option>
                  {l1Categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {error === 'validation' && !storyForm.l1_category.trim() && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    L1 Category is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="story_l2_category">
                  L2 Category <span className="required">*</span>
                </label>
                <select
                  id="story_l2_category"
                  value={storyForm.l2_category}
                  onChange={e =>
                    handleStoryFormChange('l2_category', e.target.value)
                  }
                  className="form-input"
                  disabled={!storyForm.l1_category}
                >
                  <option value="">Select L2 category</option>
                  {l2Categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {error === 'validation' && !storyForm.l2_category.trim() && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    L2 Category is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="story_author">
                  Author <span className="required">*</span>
                </label>
                <select
                  id="story_author"
                  value={storyForm.author}
                  onChange={e =>
                    handleStoryFormChange('author', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="">Select author</option>
                  {authors.map(author => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
                {error === 'validation' && !storyForm.author.trim() && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    Author is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="story_duration">
                  Duration (seconds) <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="story_duration"
                  value={storyForm.duration}
                  onChange={e =>
                    handleStoryFormChange('duration', parseInt(e.target.value))
                  }
                  placeholder="Enter duration"
                  className="form-input"
                />
                {error === 'validation' && !storyForm.duration && (
                  <span
                    style={{
                      color: '#dc3545',
                      fontSize: '14px',
                      marginTop: '4px',
                      display: 'block',
                    }}
                  >
                    Duration is required
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="content_life_in_days">
                  Content Life (days)
                </label>
                <input
                  type="number"
                  id="content_life_in_days"
                  value={storyForm.content_life_in_days}
                  onChange={e =>
                    handleStoryFormChange(
                      'content_life_in_days',
                      parseInt(e.target.value)
                    )
                  }
                  placeholder="Enter content life in days"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="story_image_url">Image URL</label>
                <input
                  type="url"
                  id="story_image_url"
                  value={storyForm.image_url}
                  onChange={e =>
                    handleStoryFormChange('image_url', e.target.value)
                  }
                  placeholder="Enter image URL"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="story_type">Type</label>
                <input
                  type="text"
                  id="story_type"
                  value={storyForm.type}
                  onChange={e => handleStoryFormChange('type', e.target.value)}
                  placeholder="Enter story type"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="story_script">Script</label>
                <textarea
                  id="story_script"
                  value={storyForm.script}
                  onChange={e =>
                    handleStoryFormChange('script', e.target.value)
                  }
                  placeholder="Enter story script"
                  className="form-input"
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={storyForm.is_active}
                    onChange={e =>
                      handleStoryFormChange('is_active', e.target.checked)
                    }
                    className="form-checkbox"
                  />
                  <span>Is Active</span>
                </label>
              </div>

              <div className="form-group">
                <label>Tags</label>
                {tagsLoading ? (
                  <div className="tags-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading tags...</span>
                  </div>
                ) : storyForm.language ? (
                  availableTags.filter(
                    tag => tag.language === storyForm.language
                  ).length > 0 ? (
                    <div className="tag-selection-container">
                      <div className="tag-grid">
                        {availableTags
                          .filter(tag => tag.language === storyForm.language)
                          .map(tag => (
                            <div
                              key={tag.id}
                              className={`tag-item ${
                                storyForm.tags.includes(tag.name)
                                  ? 'selected'
                                  : ''
                              }`}
                              onClick={() => {
                                const isSelected = storyForm.tags.includes(
                                  tag.name
                                );
                                const updatedTags = isSelected
                                  ? storyForm.tags.filter(t => t !== tag.name)
                                  : [...storyForm.tags, tag.name];
                                handleStoryFormChange('tags', updatedTags);
                              }}
                            >
                              <div className="tag-content">
                                <span className="tag-label">{tag.name}</span>
                                {storyForm.tags.includes(tag.name) && (
                                  <svg
                                    className="tag-check-icon"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path d="M20 6L9 17l-5-5" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                      {storyForm.tags.length > 0 && (
                        <div className="selected-tags-preview">
                          <span className="selected-count">
                            {storyForm.tags.length} tag
                            {storyForm.tags.length !== 1 ? 's' : ''} selected
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="no-tags-message">
                      <span>No tags available for {storyForm.language}</span>
                    </div>
                  )
                ) : (
                  <div className="no-tags-message">
                    <span>Please select a language first</span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowAddStoryModal(false);
                  setEditingStory(null);
                  setError(null);
                  setStoryForm({
                    title: '',
                    l0_category: '',
                    l1_category: '',
                    l2_category: '',
                    tags: [],
                    author: profile?.id || user?.id || '',
                    duration: 5,
                    language: '',
                    image_url: '',
                    is_active: true,
                    type: '',
                    content_life_in_days: 7,
                    script: '',
                  });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitStory}
                disabled={submittingStory}
              >
                {submittingStory ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingStory ? 'Updating Story...' : 'Creating Story...'}
                  </>
                ) : editingStory ? (
                  'Update Story'
                ) : (
                  'Create Story'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Author Modal */}
      {showAddAuthorModal && (
        <div className="modal-overlay">
          <div className="modal-content add-tag-modal">
            <div className="modal-header">
              <h2>{editingAuthor ? 'Edit Author' : 'Add New Author'}</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowAddAuthorModal(false);
                  setEditingAuthor(null);
                  setAuthorForm({ name: '', profile_picture: '', bio: '' });
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="author_name">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="author_name"
                  value={authorForm.name}
                  onChange={e =>
                    setAuthorForm({ ...authorForm, name: e.target.value })
                  }
                  placeholder="Enter author name"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="author_profile_picture">
                  Profile Picture URL <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="author_profile_picture"
                  value={authorForm.profile_picture}
                  onChange={e =>
                    setAuthorForm({
                      ...authorForm,
                      profile_picture: e.target.value,
                    })
                  }
                  placeholder="Enter profile picture URL"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="author_bio">Bio</label>
                <textarea
                  id="author_bio"
                  value={authorForm.bio}
                  onChange={e =>
                    setAuthorForm({ ...authorForm, bio: e.target.value })
                  }
                  placeholder="Enter author bio"
                  className="form-input"
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowAddAuthorModal(false);
                  setEditingAuthor(null);
                  setAuthorForm({ name: '', profile_picture: '', bio: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitAuthor}
                disabled={submittingAuthor}
              >
                {submittingAuthor ? (
                  <>
                    <div className="button-spinner"></div>
                    {editingAuthor
                      ? 'Updating Author...'
                      : 'Creating Author...'}
                  </>
                ) : editingAuthor ? (
                  'Update Author'
                ) : (
                  'Create Author'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Notification Story Modal */}
      {editingNotificationStory && (
        <div className="modal-overlay">
          <div
            className="modal-content add-challenge-modal"
            style={{ maxWidth: '560px' }}
          >
            <div className="modal-header">
              <h2>Edit Story</h2>
              <button
                className="modal-close"
                onClick={() => setEditingNotificationStory(null)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {(
                [
                  ['title', 'Title', 'text'],
                  ['language', 'Language', 'text'],
                  ['image_url', 'Image URL', 'text'],
                  ['type', 'Type', 'text'],
                  ['duration', 'Duration (sec)', 'number'],
                  ['content_life_in_days', 'Content Life (days)', 'number'],
                ] as [string, string, string][]
              ).map(([field, label, type]) => (
                <div className="form-group" key={field}>
                  <label>{label}</label>
                  <input
                    type={type}
                    className="form-input"
                    value={notificationStoryForm[field] ?? ''}
                    onChange={e =>
                      setNotificationStoryForm((prev: any) => ({
                        ...prev,
                        [field]:
                          type === 'number'
                            ? Number(e.target.value)
                            : e.target.value,
                      }))
                    }
                  />
                </div>
              ))}
              <div className="form-group">
                <label>Is Active</label>
                <select
                  className="form-input"
                  value={notificationStoryForm.is_active ? 'true' : 'false'}
                  onChange={e =>
                    setNotificationStoryForm((prev: any) => ({
                      ...prev,
                      is_active: e.target.value === 'true',
                    }))
                  }
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div
                style={{
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '16px',
                  marginTop: '4px',
                }}
              >
                <p
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#475569',
                    marginBottom: '12px',
                  }}
                >
                  Notification Data
                </p>
                {(
                  [
                    ['nt', 'Notification Title (nt)'],
                    ['nb', 'Notification Body (nb)'],
                    ['li', 'Large Image URL (li)'],
                    ['bi', 'Body Image URL (bi)'],
                  ] as [string, string][]
                ).map(([field, label]) => (
                  <div className="form-group" key={field}>
                    <label>{label}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={
                        notificationStoryForm.notification_data?.[field] ?? ''
                      }
                      onChange={e =>
                        setNotificationStoryForm((prev: any) => ({
                          ...prev,
                          notification_data: {
                            ...(prev.notification_data || {}),
                            [field]: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setEditingNotificationStory(null)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={submittingNotificationStory}
                onClick={async () => {
                  setSubmittingNotificationStory(true);
                  try {
                    const token = localStorage.getItem('authToken');
                    const { storyId, ...rest } = editingNotificationStory;
                    const payload = {
                      ...rest,
                      ...notificationStoryForm,
                    };
                    const res = await fetch(
                      `${config.API_BASE_URL}/story/${storyId}`,
                      {
                        method: 'PUT',
                        headers: {
                          accept: 'application/json',
                          'Content-Type': 'application/json',
                          ...(token && { 'x-auth-token': token }),
                        },
                        body: JSON.stringify(payload),
                      }
                    );
                    if (!res.ok) throw new Error(`Failed: ${res.status}`);
                    alert('Story updated successfully!');
                    setEditingNotificationStory(null);
                  } catch {
                    alert('Failed to update story.');
                  } finally {
                    setSubmittingNotificationStory(false);
                  }
                }}
              >
                {submittingNotificationStory ? 'Updating...' : 'Update Story'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
